import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useDroppable,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { editorConfiguration, withParagraphTools } from "../../../utils/ckEditorUtils";
import { CkeditorConfiguration } from "../../smartCharts/CkEditorConfig";
import { MyUploadAdapterPlugin } from "../../smartCharts/CustomImageUploadPlugin";
import { showAlert } from "../../../config/utils";
import ReactTooltip from "react-tooltip";
import Portal from "../../common/components/Portal";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Loader from "../../common/components/Loader";
import AddExecutiveSummaryModal from "./AddExecutiveSummaryModal";
import {
    ExecutiveSummaryBulkBandsToolbar,
    useExecutiveSummaryBulkBandsToggle
} from "./ExecutiveSummaryBulkBandsToolbar";
import {
    buildBuildingImageMap,
    clampExecutiveSummaryImageData,
    collectBuildingImageLookupIds,
    EXEC_SUMMARY_IMAGE_BAND_MAX_IMAGES,
    getExecSummaryImageData,
    getExecSummaryImageLayout,
    formatExecutiveImageBandSubheading,
    formatExecutiveImageBandLayoutOnly,
    isExecutiveSummaryImageBand,
    isExecutiveSummaryCombBand,
    isExecutiveSummaryTradeBand,
    isExecutiveSummarySampleTextBand,
    getExecutiveSummaryBandSampleText,
    getCombBandChildren,
    getBandAtPath,
    getExecutiveSummaryBandLabel,
    resolveExecSummaryImageDataEntryDisplaySrc,
    resolveExecutiveSummaryImageSrc,
    getBuildingImageRecordId,
    mergeExecutiveSummaryBuildingPickSlots,
    normalizeExecSummaryImageDataForEditor,
    EXEC_SUMMARY_IMAGE_LAYOUT,
    sanitizeRbdId,
    bandPathToRowKey,
    updateBandAtPath,
    removeBandAtPath,
    cloneExecutiveSummaryBandsForEditor,
    reorderBandAtPath,
    moveBandBetweenContainers,
    executiveBandsEqualForSave,
    buildExecutiveSummarySearchMatches,
    formatCombBandMeta,
    getExecutiveSummaryRemoveBandConfirmMessage,
    getExecutiveSummaryImageSlotCaption,
    findSearchOccurrences,
    textMatchesExecSumSearch,
    collectExecutiveSummarySearchExpandKeys,
    stripExecutiveSummaryHtml,
    formatExecutiveSummaryBandBuildingLabel,
    getExecutiveSummaryBandBuildings
} from "./executiveSummaryUtils";
import {
    getExecutiveSummaryBandQaStatus,
    getExecutiveSummaryQaStatusBreakdown,
    isExecutiveSummaryBandContentLocked,
    isExecutiveSummaryBandContentPatch
} from "./executiveSummaryQaStatus";
import ExecutiveSummaryBandQaStatus from "./ExecutiveSummaryBandQaStatus";
import ExecutiveSummaryQaProgressCard from "./ExecutiveSummaryQaProgressCard";
import ExecutiveSummarySampleTextViewer from "./ExecutiveSummarySampleTextViewer";
import {
    computeSampleTextViewerLayoutForBandRow
} from "./executiveSummarySampleTextViewerLayout";
import "./ExecutiveSummaryBandsPanel.css";

const EXEC_SUM_BAND_BUILDINGS_INITIAL_COUNT = 10;
const EXEC_SUM_EDITOR_READONLY_ID = "exec-sum-content-lock";
const EXEC_SUM_BAND_ACCORDION_EXPAND_TIP = "Expand band";
const EXEC_SUM_BAND_ACCORDION_COLLAPSE_TIP = "Collapse band";
const EXEC_SUM_BAND_FULL_EDITOR_TIP = "Open full-screen editor";
const EXEC_SUM_BAND_SAMPLE_TEXT_FULL_EDITOR_TIP = "Open sample text full-screen editor";
const EXEC_SUM_BAND_SAMPLE_TEXT_VIEW_TIP = "View sample text";
const EXEC_SUM_BAND_RENAME_TIP = "Rename band";
const EXEC_SUM_BAND_REMOVE_TIP = "Remove band";
const EXEC_SUM_GROUP_EXPAND_BANDS_TIP = "Expand bands in this group";
const EXEC_SUM_GROUP_COLLAPSE_BANDS_TIP = "Collapse bands in this group";

const THUMB_PH = "/img/no-image.svg";
const EXEC_SUM_SEARCH_DEBOUNCE_MS = 250;
const EXEC_SUM_SEARCH_MATCH_CASE_TIP =
    'When turned on, upper and lower case matter. For example, searching "roof" won\'t find "Roof".<br />Alt+C to toggle';
const EXEC_SUM_SEARCH_WHOLE_WORD_TIP =
    'When turned on, only whole words are matched. For example, "park" won\'t find "parking".<br />Alt+W to toggle';

/** Fast CKEditor find highlight: re-scan only when query changes; step or jump index otherwise. */
function applyExecSumCkEditorFindHighlight(editor, query, occurrenceIndex, navRef, findOptions = {}) {
    if (!editor) return;

    let editing;
    try {
        editing = editor.plugins.get("FindAndReplaceEditing");
    } catch {
        return;
    }
    const state = editing?.state;
    if (!state) return;

    const { matchCase = false, wholeWord = false } = findOptions;
    const navKey = `${query}\0${matchCase}\0${wholeWord}`;

    if (!query) {
        editing.stop();
        navRef.current = { key: "", index: -1 };
        return;
    }

    const prev = navRef.current;
    const searchChanged = prev.key !== navKey;

    if (searchChanged || !state.results.length || state.searchText !== query) {
        editor.execute("find", query, { matchCase, wholeWords: wholeWord });
        prev.key = navKey;
        prev.index = -1;
    }

    if (!state.results.length) return;

    const targetIndex = Math.min(Math.max(occurrenceIndex, 0), state.results.length - 1);
    if (targetIndex === prev.index && state.highlightedResult === state.results.get(targetIndex)) {
        return;
    }

    const delta = prev.index >= 0 ? targetIndex - prev.index : targetIndex;
    if (!searchChanged && prev.index >= 0 && delta !== 0 && Math.abs(delta) <= 2) {
        const command = delta > 0 ? "findNext" : "findPrevious";
        for (let i = 0; i < Math.abs(delta); i++) {
            editor.execute(command);
        }
    } else {
        state.highlightedResult = state.results.get(targetIndex);
    }

    prev.index = targetIndex;
}

function highlightExecSumSearchLabel(label, query, searchOptions = {}) {
    const text = label || "—";
    const q = String(query ?? "").trim();
    if (!q) return text;

    const occurrences = findSearchOccurrences(text, q, searchOptions);
    if (!occurrences.length) return text;

    const parts = [];
    let lastIndex = 0;
    occurrences.forEach((occ, i) => {
        if (occ.start > lastIndex) parts.push(text.slice(lastIndex, occ.start));
        parts.push(
            <mark key={`${occ.start}-${i}`} className="exec-sum-search-highlight">
                {text.slice(occ.start, occ.end)}
            </mark>
        );
        lastIndex = occ.end;
    });
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return <>{parts}</>;
}
const DND_RESIZE_OBSERVER_CONFIG = { disabled: true };

const ORG_CONTAINER_TOP = "org-container-top";

function orgContainerGroup(groupIndex) {
    return `org-container-group-${groupIndex}`;
}

function orgItemId(path) {
    return `org-item-${(Array.isArray(path) ? path : []).join("-")}`;
}

function parseOrgItemId(id) {
    const sid = String(id ?? "");
    if (!sid.startsWith("org-item-")) return null;
    const parts = sid
        .replace("org-item-", "")
        .split("-")
        .map(p => Number(p));
    if (!parts.length || parts.some(p => !Number.isInteger(p) || p < 0)) return null;
    if (parts.length > 2) return null;
    return parts;
}

function parseOrgGroupContainerId(id) {
    const sid = String(id ?? "");
    const match = sid.match(/^org-container-group-(\d+)$/);
    if (!match) return null;
    const idx = Number(match[1]);
    return Number.isInteger(idx) && idx >= 0 ? idx : null;
}

function orgBandStableKey(band) {
    if (!band || typeof band !== "object") return "";
    const identity = band._execClientId ?? band.id ?? band.key ?? getExecutiveSummaryBandLabel(band);
    return identity != null && String(identity) !== "" ? String(identity) : "";
}

function applyOrganizeDragOperation(bands, activeId, overId) {
    const fromPath = parseOrgItemId(activeId);
    if (!fromPath) return bands;
    const moving = getBandAtPath(bands, fromPath);
    if (!moving) return bands;

    const overPath = parseOrgItemId(overId);
    const overGroupContainerIdx = parseOrgGroupContainerId(overId);

    if (overId === ORG_CONTAINER_TOP) {
        if (fromPath.length !== 2) return bands;
        return moveBandBetweenContainers(bands, fromPath, null, bands.length);
    }

    if (overGroupContainerIdx != null) {
        if (isExecutiveSummaryCombBand(moving)) return bands;
        if (!isExecutiveSummaryCombBand(bands[overGroupContainerIdx])) return bands;
        const childLen = getCombBandChildren(bands[overGroupContainerIdx]).length;
        return moveBandBetweenContainers(bands, fromPath, [overGroupContainerIdx], childLen);
    }

    if (!overPath) return bands;

    if (fromPath.length === overPath.length) {
        if (fromPath.length === 1 && fromPath[0] !== overPath[0]) {
            return reorderBandAtPath(bands, fromPath, overPath[0]);
        }
        if (fromPath.length === 2 && fromPath[0] === overPath[0] && fromPath[1] !== overPath[1]) {
            return reorderBandAtPath(bands, fromPath, overPath[1]);
        }
    }

    if (fromPath.length === 2 && overPath.length === 1) {
        return moveBandBetweenContainers(bands, fromPath, null, overPath[0]);
    }

    if (fromPath.length === 1 && overPath.length === 2) {
        if (isExecutiveSummaryCombBand(moving)) return bands;
        const parent = bands[overPath[0]];
        if (!isExecutiveSummaryCombBand(parent)) return bands;
        return moveBandBetweenContainers(bands, fromPath, [overPath[0]], overPath[1]);
    }

    if (fromPath.length === 2 && overPath.length === 2 && fromPath[0] !== overPath[0]) {
        if (isExecutiveSummaryCombBand(moving)) return bands;
        const parent = bands[overPath[0]];
        if (!isExecutiveSummaryCombBand(parent)) return bands;
        return moveBandBetweenContainers(bands, fromPath, [overPath[0]], overPath[1]);
    }

    return bands;
}

export function bandRowKey(band, index) {
    return bandPathToRowKey([index], band);
}

function renderExpandCollapseIcon(collapsing) {
    return (
        <span
            className={`exec-sum-toolbar-btn__box-icon${
                collapsing ? " exec-sum-toolbar-btn__box-icon--minus" : " exec-sum-toolbar-btn__box-icon--plus"
            }`}
            aria-hidden="true"
        />
    );
}

function getBandRowDomId(rowKey) {
    return `exec-sum-band-${sanitizeRbdId(rowKey)}`;
}

function getScrollParent(element) {
    if (!element) return document.documentElement;
    let parent = element.parentElement;
    while (parent) {
        const { overflowY, overflow } = window.getComputedStyle(parent);
        const scrollable = /(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflow);
        if (scrollable && parent.scrollHeight > parent.clientHeight) return parent;
        parent = parent.parentElement;
    }
    return document.documentElement;
}

/** Scroll so the matched band/summary section sits below sticky panel / group headers. */
function scrollExecutiveSummaryBandIntoView(rowKey, panelHeadEl, options = {}) {
    const row = document.getElementById(getBandRowDomId(rowKey));
    if (!row) return;

    const summaryTarget = options.preferSummary ? row.querySelector(".exec-sum-comb-summary") : null;
    const bar = row.querySelector(".exec-sum-band-row__bar");
    const ownCombSticky = row.querySelector(":scope > .exec-sum-band-row__drag-shell > .exec-sum-comb-sticky-head");
    const target = bar || row;
    const finalTarget = summaryTarget || target;
    const scrollParent = getScrollParent(finalTarget);
    const parentRect =
        scrollParent === document.documentElement ? { top: 0 } : scrollParent.getBoundingClientRect();

    // Use actual sticky positions instead of raw heights; this stays correct after repeated navigation.
    let visibleTop = 12;
    const panelHeadRect = panelHeadEl?.getBoundingClientRect?.();
    if (panelHeadRect) visibleTop = Math.max(visibleTop, panelHeadRect.bottom - parentRect.top + 12);

    const combAncestor = row.closest(".exec-sum-band-row--comb");
    if (combAncestor && combAncestor !== row) {
        const combSticky = combAncestor.querySelector(".exec-sum-comb-sticky-head");
        // Child matches can settle under the group's sticky header after scrolling.
        // Reserve that header height every time for nested bands.
        if (combSticky) visibleTop += combSticky.offsetHeight + 8;
    }
    if (summaryTarget && ownCombSticky) {
        visibleTop += ownCombSticky.offsetHeight + 8;
    }

    const targetRect = finalTarget.getBoundingClientRect();
    const currentScroll = scrollParent === document.documentElement ? window.scrollY : scrollParent.scrollTop;
    const top = currentScroll + targetRect.top - parentRect.top - visibleTop;

    if (scrollParent === document.documentElement) {
        window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    } else {
        scrollParent.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    }
}

export function ExecutiveSummarySubTabs({ activeTab, onChange, variant = "embedded", showExecutiveSummaryTab = true }) {
    const wrapClass =
        variant === "top"
            ? "building-exec-sum-subtabs-wrap building-exec-sum-subtabs-wrap--top"
            : "building-exec-sum-subtabs-wrap col-md-12 px-0";
    return (
        <div className={wrapClass}>
            <div className="building-exec-sum-subtabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "overview"}
                    className={activeTab === "overview" ? "is-active" : ""}
                    onClick={() => onChange("overview")}
                >
                    Overview
                </button>
                {showExecutiveSummaryTab ? (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "executiveSummary"}
                        className={activeTab === "executiveSummary" ? "is-active" : ""}
                        onClick={() => onChange("executiveSummary")}
                    >
                        Executive Summary
                    </button>
                ) : null}
            </div>
        </div>
    );
}

function SortableImageSlot({
    slotId,
    s,
    idx,
    editable,
    dragDisabled,
    removeAt,
    renderCellInner
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: slotId,
        disabled: dragDisabled || !editable,
        resizeObserverConfig: DND_RESIZE_OBSERVER_CONFIG
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 11 : undefined
    };
    return (
        <div ref={setNodeRef} style={style} className={`exec-sum-img-cell-wrap${isDragging ? " is-dragging" : ""}`}>
            {renderCellInner(s, idx, editable ? { ...listeners, ...attributes } : null, isDragging, removeAt)}
        </div>
    );
}

function ImageBandSlotGrid({
    band,
    bandPath,
    imageMap,
    editable,
    onUpdateBand,
    bandStableIdForSlots,
    onRequestRemoveSlot,
    searchQuery = "",
    searchOptions = {},
    captionHighlightSlotIndex = null
}) {
    const layout = getExecSummaryImageLayout(band);
    const raw = getExecSummaryImageData(band);
    const filled = raw.map((entry, i) => {
        const src = entry ? resolveExecSummaryImageDataEntryDisplaySrc(entry, imageMap) : "";
        return { entry, src, key: entry?.id != null ? String(entry.id) : `slot-${i}` };
    });
    const canReorder = editable && filled.length > 1;
    let gridClass = "exec-sum-img-grid";
    if (layout === EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_STANDARD) gridClass += " exec-sum-img-grid--1";
    else if (layout === EXEC_SUMMARY_IMAGE_LAYOUT.SINGLE_WIDE) gridClass += " exec-sum-img-grid--wide";
    else if (layout === EXEC_SUMMARY_IMAGE_LAYOUT.DOUBLE) gridClass += " exec-sum-img-grid--2";
    else if (layout === EXEC_SUMMARY_IMAGE_LAYOUT.TRIPLE) gridClass += " exec-sum-img-grid--3";

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const patchData = useCallback(
        data =>
            onUpdateBand(bandPath, {
                type: "image_band",
                data: clampExecutiveSummaryImageData(data),
                config: { ...(band.config && typeof band.config === "object" ? band.config : {}), type: layout },
                band_kind: undefined,
                image_layout: undefined,
                images: undefined
            }),
        [onUpdateBand, bandPath, band, layout]
    );

    const removeAt = slotIndex => {
        patchData(normalizeExecSummaryImageDataForEditor(raw.filter((_, j) => j !== slotIndex)));
    };

    const slotSortId = key => sanitizeRbdId(`${bandStableIdForSlots}-img-${key}`);
    const itemIds = filled.map(s => slotSortId(s.key));

    const renderCellInner = (s, idx, dragHandleProps, isDragging, removeSlot) => {
        const captionText = getExecutiveSummaryImageSlotCaption(band, idx, { imageMap });
        const showCaptionHighlight =
            searchQuery &&
            captionHighlightSlotIndex === idx &&
            textMatchesExecSumSearch(captionText, searchQuery, searchOptions);

        return (
            <div className={`exec-sum-img-cell${isDragging ? " is-dragging" : ""}`}>
                {s.src ? <img src={s.src} alt="" loading="lazy" draggable={false} /> : <div className="exec-sum-img-placeholder">No preview</div>}
                {captionText ? (
                    <div className="exec-sum-img-caption small text-muted mt-1">
                        {showCaptionHighlight
                            ? highlightExecSumSearchLabel(captionText, searchQuery, searchOptions)
                            : captionText}
                    </div>
                ) : null}
            {editable ? (
                <div className="exec-sum-img-cell__actions">
                    {dragHandleProps ? (
                        <span className="exec-sum-img-cell__drag exec-sum-drag" {...dragHandleProps} aria-hidden="true">
                            <i className="fas fa-grip-vertical" />
                        </span>
                    ) : null}
                    {s.entry ? (
                        <button
                            type="button"
                            className="exec-sum-img-remove"
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => {
                                if (onRequestRemoveSlot) onRequestRemoveSlot(idx);
                                else removeSlot(idx);
                            }}
                            aria-label="Remove image"
                        >
                            ×
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
        );
    };

    const onImageSlotsDragEnd = useCallback(
        event => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const ids = filled.map(s => sanitizeRbdId(`${bandStableIdForSlots}-img-${s.key}`));
            const oldIndex = ids.indexOf(active.id);
            const newIndex = ids.indexOf(over.id);
            if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
            patchData(normalizeExecSummaryImageDataForEditor(arrayMove([...raw], oldIndex, newIndex)));
        },
        [filled, raw, bandStableIdForSlots, patchData]
    );

    if (!canReorder) {
        return (
            <div className={gridClass}>
                {filled.map((s, idx) => (
                    <React.Fragment key={s.key}>{renderCellInner(s, idx, null, false, removeAt)}</React.Fragment>
                ))}
            </div>
        );
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onImageSlotsDragEnd}>
            <SortableContext items={itemIds} strategy={rectSortingStrategy}>
                <div className={gridClass}>
                    {filled.map((s, idx) => (
                        <SortableImageSlot
                            key={s.key}
                            slotId={slotSortId(s.key)}
                            s={s}
                            idx={idx}
                            editable={editable}
                            dragDisabled={false}
                            removeAt={removeAt}
                            renderCellInner={renderCellInner}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}


function BandRowTypeIcon({ isComb, isImage }) {
    const iconClass = isComb ? "fa-folder" : isImage ? "fa-image" : "fa-align-left";
    const title = isComb ? "Group band" : isImage ? "Image band" : "Text band";
    return (
        <span className="exec-sum-band-row__type-icon" title={title} aria-hidden="true">
            <i className={`fas ${iconClass}`} />
        </span>
    );
}

function OrganizeBandRow({
    band,
    path,
    selected,
    isNestTarget,
    onSelectGroup,
    dragListeners,
    dragAttributes,
    setNodeRef,
    style,
    isDragging
}) {
    const isComb = isExecutiveSummaryCombBand(band);
    const isImage = isExecutiveSummaryImageBand(band);
    const childCount = isComb ? getCombBandChildren(band).length : 0;

    const handleRowClick = e => {
        if (isComb && onSelectGroup) {
            e.stopPropagation();
            onSelectGroup(path[0]);
        }
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={`exec-sum-order-row${isDragging ? " exec-sum-order-row--dragging" : ""}${
                selected ? " exec-sum-order-row--selected" : ""
            }${isComb ? " exec-sum-order-row--group" : ""}${isNestTarget ? " exec-sum-order-row--nest-over" : ""}`}
            onClick={handleRowClick}
            {...dragListeners}
            {...dragAttributes}
        >
            <BandRowTypeIcon isComb={isComb} isImage={isImage} />
            <span className="exec-sum-order-row__name">{getExecutiveSummaryBandLabel(band) || "Untitled band"}</span>
            {isComb ? (
                <span className="exec-sum-order-row__badge">
                    {childCount === 0 ? "Empty" : `${childCount} band${childCount === 1 ? "" : "s"}`}
                </span>
            ) : null}
        </li>
    );
}

function OrganizeDragPreview({ band }) {
    if (!band) return null;
    const isComb = isExecutiveSummaryCombBand(band);
    const isImage = isExecutiveSummaryImageBand(band);
    const childCount = isComb ? getCombBandChildren(band).length : 0;

    return (
        <div className={`exec-sum-order-row exec-sum-order-row--overlay${isComb ? " exec-sum-order-row--group" : ""}`}>
            <BandRowTypeIcon isComb={isComb} isImage={isImage} />
            <span className="exec-sum-order-row__name">{getExecutiveSummaryBandLabel(band) || "Untitled band"}</span>
            {isComb ? (
                <span className="exec-sum-order-row__badge">
                    {childCount === 0 ? "Empty" : `${childCount} band${childCount === 1 ? "" : "s"}`}
                </span>
            ) : null}
        </div>
    );
}

function SortableOrganizeBandRow(props) {
    const path = props.path;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: orgItemId(path),
        animateLayoutChanges: () => false,
        resizeObserverConfig: DND_RESIZE_OBSERVER_CONFIG
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? "none" : transition,
        zIndex: isDragging ? 20 : undefined
    };
    return (
        <OrganizeBandRow
            {...props}
            setNodeRef={setNodeRef}
            style={style}
            isDragging={isDragging}
            dragListeners={listeners}
            dragAttributes={attributes}
        />
    );
}

function OrganizePaneContainer({
    id,
    className,
    children,
    emptyLabel,
    isOverHighlight,
    dropLabel = "Drop here",
    showDropPlaceholder = false
}) {
    const { setNodeRef, isOver } = useDroppable({
        id,
        resizeObserverConfig: DND_RESIZE_OBSERVER_CONFIG
    });
    const hasChildren = React.Children.count(children) > 0;
    return (
        <div
            ref={setNodeRef}
            className={`exec-sum-organize-pane__drop${isOverHighlight && isOver ? " exec-sum-organize-pane__drop--over" : ""}${
                showDropPlaceholder ? " exec-sum-organize-pane__drop--with-placeholder" : ""
            }`}
        >
            <ul className={className} data-drop-label={dropLabel}>
                {children}
                {showDropPlaceholder ? (
                    <li className={`exec-sum-organize-placeholder${isOver ? " exec-sum-organize-placeholder--over" : ""}`}>
                        {isOver ? "Release to move to top level" : "Drop here to move to top level"}
                    </li>
                ) : null}
            </ul>
            {!hasChildren && emptyLabel ? (
                <div className="exec-sum-organize-pane__empty">{emptyLabel}</div>
            ) : null}
        </div>
    );
}

function OrganizeExpandedGroupPane({ groupIndex, group, draftBands, draggingLeaf, onCloseGroup }) {
    const stableKey = orgBandStableKey(group);
    const label = getExecutiveSummaryBandLabel(group) || "Untitled group";
    const childPaths = useMemo(
        () => getCombBandChildren(group).map((_, i) => [groupIndex, i]),
        [group, groupIndex]
    );
    const childItemIds = useMemo(() => childPaths.map(path => orgItemId(path)), [childPaths]);

    return (
        <div className="exec-sum-organize-group-pane">
            <div className="exec-sum-organize-group-pane__head">
                <h6 className="exec-sum-organize-pane__title mb-0">{label}</h6>
                <button
                    type="button"
                    className="exec-sum-organize-group-pane__close not-draggable"
                    aria-label={`Close ${label}`}
                    onClick={e => {
                        e.stopPropagation();
                        onCloseGroup(stableKey);
                    }}
                >
                    Close
                </button>
            </div>
            <p className="exec-sum-organize-pane__hint small text-muted">
                Drop leaf bands here to nest them. Reorder nested bands here, or drag them left to move out.
            </p>
            <SortableContext items={childItemIds} strategy={verticalListSortingStrategy}>
                <OrganizePaneContainer
                    id={orgContainerGroup(groupIndex)}
                    className="exec-sum-order-list"
                    emptyLabel="No bands in this group yet. Drag bands here from the left."
                    isOverHighlight={draggingLeaf}
                    dropLabel="Nest in this group"
                >
                    {childPaths.map(path => {
                        const band = getBandAtPath(draftBands, path);
                        if (!band) return null;
                        return <SortableOrganizeBandRow key={orgItemId(path)} band={band} path={path} />;
                    })}
                </OrganizePaneContainer>
            </SortableContext>
        </div>
    );
}

function OrganizeBandsModal({ bands, onApply, onClose, saving = false, autoSave = false }) {
    const [draftBands, setDraftBands] = useState(() => cloneExecutiveSummaryBandsForEditor(bands));
    const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
    const [activeDragPath, setActiveDragPath] = useState(null);
    const [applying, setApplying] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const orderSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        setDraftBands(cloneExecutiveSummaryBandsForEditor(bands));
        setExpandedGroupKeys([]);
    }, [bands]);

    useEffect(() => {
        setExpandedGroupKeys(prev =>
            prev.filter(key =>
                draftBands.some(band => isExecutiveSummaryCombBand(band) && orgBandStableKey(band) === key)
            )
        );
    }, [draftBands]);

    const organizeDirty = useMemo(() => !executiveBandsEqualForSave(bands, draftBands), [bands, draftBands]);

    const topLevelPaths = useMemo(
        () => (Array.isArray(draftBands) ? draftBands : []).map((_, i) => [i]),
        [draftBands]
    );

    const topItemIds = useMemo(() => topLevelPaths.map(path => orgItemId(path)), [topLevelPaths]);

    const expandedGroupIndices = useMemo(
        () =>
            draftBands.reduce((indices, band, index) => {
                if (isExecutiveSummaryCombBand(band) && expandedGroupKeys.includes(orgBandStableKey(band))) {
                    indices.push(index);
                }
                return indices;
            }, []),
        [draftBands, expandedGroupKeys]
    );

    const hasExpandedGroups = expandedGroupIndices.length > 0;

    const activeDragBand = activeDragPath ? getBandAtPath(draftBands, activeDragPath) : null;
    const draggingLeaf = activeDragBand && !isExecutiveSummaryCombBand(activeDragBand);
    const draggingFromGroup = activeDragPath?.length === 2;

    const toggleGroupAtIndex = useCallback(
        groupIndex => {
            const band = draftBands[groupIndex];
            if (!isExecutiveSummaryCombBand(band)) return;
            const key = orgBandStableKey(band);
            setExpandedGroupKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
        },
        [draftBands]
    );

    const closeExpandedGroup = useCallback(stableKey => {
        setExpandedGroupKeys(prev => prev.filter(k => k !== stableKey));
    }, []);

    const onOrganizeDragStart = event => {
        setActiveDragPath(parseOrgItemId(event.active.id));
    };

    const onOrganizeDragCancel = () => {
        setActiveDragPath(null);
    };

    const onOrganizeDragEnd = event => {
        const { active, over } = event;
        setActiveDragPath(null);
        if (!over || active.id === over.id) return;

        const overPath = parseOrgItemId(over.id);
        const overGroupContainerIdx = parseOrgGroupContainerId(over.id);

        setDraftBands(prev => {
            const next = applyOrganizeDragOperation(prev, active.id, over.id);
            return next === prev ? prev : next;
        });

        const targetGroupIndex =
            overGroupContainerIdx != null ? overGroupContainerIdx : overPath?.length === 2 ? overPath[0] : null;
        const targetGroup = targetGroupIndex != null ? draftBands[targetGroupIndex] : null;
        if (isExecutiveSummaryCombBand(targetGroup)) {
            const key = orgBandStableKey(targetGroup);
            setExpandedGroupKeys(prev => (prev.includes(key) ? prev : [...prev, key]));
        }
    };

    const applyOrder = async () => {
        if (applying || saving || !organizeDirty) return;
        setApplying(true);
        try {
            await onApply(draftBands);
        } finally {
            setApplying(false);
        }
    };

    const savingOrApplying = saving || applying;

    const requestClose = () => {
        if (savingOrApplying) return;
        if (organizeDirty) {
            setShowCancelConfirm(true);
            return;
        }
        onClose();
    };

    const confirmDiscardClose = () => {
        setShowCancelConfirm(false);
        onClose();
    };

    const modalContent = (
        <div className="modal modal-region modal-view inbox-modal" style={{ display: "block" }} tabIndex="-1" role="dialog">
            <Draggable handle=".exec-sum-organize-modal__drag-handle" cancel=".close, .not-draggable">
                <div
                    className={`modal-dialog exec-sum-organize-modal${
                        hasExpandedGroups ? " exec-sum-organize-modal--expanded" : ""
                    }`}
                    role="document"
                >
                    <div className="modal-content">
                        <div className="modal-header exec-sum-organize-modal__drag-handle">
                            <div>
                                <h5 className="modal-title">Organize bands</h5>
                                <p className="exec-sum-organize-modal__subtitle mb-0">
                                    Reorder sections and move bands between groups without changing editor content.
                                </p>
                            </div>
                            <button type="button" className="close" onClick={requestClose} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body exec-sum-organize-modal__body not-draggable">
                            <DndContext
                                sensors={orderSensors}
                                collisionDetection={closestCenter}
                                onDragStart={onOrganizeDragStart}
                                onDragCancel={onOrganizeDragCancel}
                                onDragEnd={onOrganizeDragEnd}
                            >
                                <div className="exec-sum-organize-layout">
                                    <div className="exec-sum-organize-pane exec-sum-organize-pane--left">
                                        <h6 className="exec-sum-organize-pane__title">Top level</h6>
                                        <p className="exec-sum-organize-pane__hint small text-muted">
                                            Drag here to reorder top-level sections only.
                                        </p>
                                        <SortableContext items={topItemIds} strategy={verticalListSortingStrategy}>
                                            <OrganizePaneContainer
                                                id={ORG_CONTAINER_TOP}
                                                className="exec-sum-order-list"
                                                isOverHighlight={draggingFromGroup}
                                                dropLabel="Move to top level"
                                                showDropPlaceholder={draggingFromGroup}
                                            >
                                                {topLevelPaths.map(path => {
                                                    const band = getBandAtPath(draftBands, path);
                                                    if (!band) return null;
                                                    const bandKey = orgBandStableKey(band);
                                                    return (
                                                        <SortableOrganizeBandRow
                                                            key={orgItemId(path)}
                                                            band={band}
                                                            path={path}
                                                            selected={
                                                                isExecutiveSummaryCombBand(band) &&
                                                                expandedGroupKeys.includes(bandKey)
                                                            }
                                                            isNestTarget={false}
                                                            onSelectGroup={toggleGroupAtIndex}
                                                        />
                                                    );
                                                })}
                                            </OrganizePaneContainer>
                                        </SortableContext>
                                    </div>

                                    <div className="exec-sum-organize-pane exec-sum-organize-pane--right">
                                        {hasExpandedGroups ? (
                                            <div className="exec-sum-organize-groups-stack">
                                                {expandedGroupIndices.map(groupIndex => {
                                                    const group = draftBands[groupIndex];
                                                    if (!group || !isExecutiveSummaryCombBand(group)) return null;
                                                    return (
                                                        <OrganizeExpandedGroupPane
                                                            key={orgBandStableKey(group)}
                                                            groupIndex={groupIndex}
                                                            group={group}
                                                            draftBands={draftBands}
                                                            draggingLeaf={draggingLeaf}
                                                            onCloseGroup={closeExpandedGroup}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="exec-sum-organize-hints">
                                                <h6 className="exec-sum-organize-pane__title">How to organize</h6>
                                                <ul className="exec-sum-organize-hints__list">
                                                    <li>Drag bands in the left list to reorder top-level sections.</li>
                                                    <li>Click a group to expand it and view its nested bands on the right.</li>
                                                    <li>Expand multiple groups to drag bands directly between them.</li>
                                                    <li>Drag a leaf band into an expanded group to nest it.</li>
                                                    <li>Drag a band out of a group back to the left list to unnest it.</li>
                                                    <li>Use Close on the right to collapse a group you opened by mistake.</li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {createPortal(
                                    <DragOverlay dropAnimation={null} zIndex={10050}>
                                        <OrganizeDragPreview band={activeDragBand} />
                                    </DragOverlay>,
                                    document.body
                                )}
                            </DndContext>
                        </div>
                        <div className="modal-footer not-draggable">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={requestClose}
                                disabled={savingOrApplying}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={applyOrder}
                                disabled={savingOrApplying || !organizeDirty}
                            >
                                {savingOrApplying ? (autoSave ? "Saving..." : "Applying...") : autoSave ? "Apply & save" : "Apply"}
                            </button>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>
    );

    return (
        <>
            <Portal body={modalContent} onCancel={requestClose} />
            {showCancelConfirm ? (
                <Portal
                    body={
                        <ConfirmationModal
                            heading="Do you want to clear and lose all changes?"
                            type="cancel"
                            message="This action cannot be reverted, are you sure that you need to cancel?"
                            onNo={() => setShowCancelConfirm(false)}
                            onYes={confirmDiscardClose}
                        />
                    }
                    onCancel={() => setShowCancelConfirm(false)}
                />
            ) : null}
        </>
    );
}

function ExecutiveSummaryLogCompareTextPreview({ value }) {
    return (
        <div
            className="exec-sum-log-compare-html-preview ck-content"
            dangerouslySetInnerHTML={{ __html: value || "-" }}
        />
    );
}

function ExecutiveSummaryTextEditor({
    band,
    bandPath,
    editable,
    patchBandAtPath,
    contentField = "value",
    searchHighlightQuery = "",
    searchHighlightOccurrenceIndex = 0,
    isSearchHighlightActive = false,
    searchFindOptions = {},
    wrapperClassName = "",
    optimizeTyping = false
}) {
    const editorRef = useRef(null);
    const findNavRef = useRef({ key: "", index: -1 });
    const getBandContent = useCallback(
        () => (contentField === "sample_text" ? getExecutiveSummaryBandSampleText(band) : band.value ?? ""),
        [band, contentField]
    );
    const bandValueRef = useRef(getBandContent());
    const bandPathKey = Array.isArray(bandPath) ? bandPath.join(".") : String(bandPath ?? "");
    bandValueRef.current = getBandContent();
    const highlightRef = useRef({
        active: isSearchHighlightActive,
        query: searchHighlightQuery,
        index: searchHighlightOccurrenceIndex,
        findOptions: searchFindOptions
    });
    highlightRef.current = {
        active: isSearchHighlightActive,
        query: searchHighlightQuery,
        index: searchHighlightOccurrenceIndex,
        findOptions: searchFindOptions
    };

    const editorConfig = useMemo(
        () => ({
            ...withParagraphTools(CkeditorConfiguration),
            extraPlugins: [MyUploadAdapterPlugin]
        }),
        []
    );

    const readOnlyEditorConfig = useMemo(
        () => ({
            ...editorConfiguration,
            toolbar: []
        }),
        []
    );

    const qaStatus = getExecutiveSummaryBandQaStatus(band);
    const editorInstanceKey = `${bandPathKey}-${contentField}-${qaStatus ?? "none"}-${editable ? "edit" : "view"}`;

    const syncFindHighlight = useCallback(() => {
        const { active, query, index, findOptions } = highlightRef.current;
        applyExecSumCkEditorFindHighlight(
            editorRef.current,
            active ? query : "",
            index,
            findNavRef,
            findOptions
        );
    }, []);

    useEffect(() => {
        if (!editorRef.current) return undefined;
        const frameId = window.requestAnimationFrame(syncFindHighlight);
        return () => window.cancelAnimationFrame(frameId);
    }, [
        isSearchHighlightActive,
        searchHighlightQuery,
        searchHighlightOccurrenceIndex,
        searchFindOptions.matchCase,
        searchFindOptions.wholeWord,
        syncFindHighlight
    ]);


    const handleEditorReady = useCallback(
        editor => {
            editorRef.current = editor;
            editor.setData(bandValueRef.current);
            findNavRef.current = { key: "", index: -1 };
            if (!editable) {
                editor.enableReadOnlyMode(EXEC_SUM_EDITOR_READONLY_ID);
            }
            window.requestAnimationFrame(syncFindHighlight);
        },
        [editable, syncFindHighlight]
    );

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor?.enableReadOnlyMode) return;
        if (editable) {
            if (editor.isReadOnly) {
                editor.disableReadOnlyMode(EXEC_SUM_EDITOR_READONLY_ID);
            }
        } else {
            editor.enableReadOnlyMode(EXEC_SUM_EDITOR_READONLY_ID);
        }
    }, [editable]);

    useEffect(() => {
        if (!optimizeTyping || !editorRef.current) return;
        const editor = editorRef.current;
        if (editor.ui?.focusTracker?.isFocused) return;
        const nextValue = getBandContent();
        if (editor.getData() !== nextValue) {
            editor.setData(nextValue);
        }
    }, [band, contentField, bandPathKey, optimizeTyping, getBandContent]);

    useEffect(() => {
        findNavRef.current = { key: "", index: -1 };
    }, [bandPath, searchHighlightQuery, isSearchHighlightActive, searchFindOptions.matchCase, searchFindOptions.wholeWord]);

    const wrapperClasses = [
        "ck-form-control",
        "exec-sum-text-editor",
        wrapperClassName ? "form-group ckeditor-band recom-ck-band" : "",
        wrapperClassName
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={wrapperClasses}>
            <CKEditor
                key={editorInstanceKey}
                editor={Editor}
                config={editable ? editorConfig : readOnlyEditorConfig}
                {...(optimizeTyping && editable ? {} : { data: getBandContent() })}
                onChange={(evt, editor) => {
                    if (!editable) return;
                    patchBandAtPath(bandPath, { [contentField]: editor.getData() });
                }}
                onReady={handleEditorReady}
            />
        </div>
    );
}

function renderExecutiveSummaryTextEditor(
    band,
    bandPath,
    editable,
    patchBandAtPath,
    textEditorSearchProps = {},
    editorOptions = {}
) {
    return (
        <ExecutiveSummaryTextEditor
            band={band}
            bandPath={bandPath}
            editable={editable}
            patchBandAtPath={patchBandAtPath}
            {...textEditorSearchProps}
            {...editorOptions}
        />
    );
}

function CollapsibleTextEditorSection({
    title,
    helperText,
    defaultOpen = false,
    band,
    bandPath,
    contentField = "value",
    editable,
    patchBandAtPath,
    onOpenFullEditor,
    bandFullEditorTooltipId,
    fullEditorTip = EXEC_SUM_BAND_FULL_EDITOR_TIP,
    optimizeTyping = false,
    textEditorSearchProps = {}
}) {
    const [sectionOpen, setSectionOpen] = useState(defaultOpen);
    const { isSearchHighlightActive = false, openSummaryForSearch = false } = textEditorSearchProps;

    useEffect(() => {
        if (isSearchHighlightActive || openSummaryForSearch) setSectionOpen(true);
    }, [isSearchHighlightActive, openSummaryForSearch]);

    const toggleSection = e => {
        if (e) e.stopPropagation();
        setSectionOpen(prev => !prev);
    };

    const handleBarKeyDown = e => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleSection(e);
        }
    };

    return (
        <div className={`exec-sum-comb-summary exec-sum-collapsible-editor${sectionOpen ? " exec-sum-comb-summary--open" : ""}`}>
            <div
                className="exec-sum-comb-summary__bar"
                role="button"
                tabIndex={0}
                aria-expanded={sectionOpen}
                aria-label={sectionOpen ? `${title}, expanded` : `${title}, collapsed`}
                onClick={toggleSection}
                onKeyDown={handleBarKeyDown}
            >
                <div className="exec-sum-comb-summary__bar-main">
                    <span className="exec-sum-comb-summary__title">{title}</span>
                    <i
                        className={`fas exec-sum-comb-summary__chevron ${sectionOpen ? "fa-chevron-up" : "fa-chevron-down"}`}
                        aria-hidden="true"
                    />
                </div>
                {editable && onOpenFullEditor && sectionOpen ? (
                    <button
                        type="button"
                        className="exec-sum-band-row__icon-btn"
                        onClick={e => {
                            e.stopPropagation();
                            onOpenFullEditor(bandPath);
                        }}
                        aria-label={fullEditorTip}
                        data-tip={fullEditorTip}
                        data-for={bandFullEditorTooltipId}
                    >
                        <img src="/img/expand1.svg" alt="" width="18" height="18" />
                    </button>
                ) : null}
            </div>
            {sectionOpen ? (
                <div className="exec-sum-comb-summary__body">
                    {helperText ? <p className="exec-sum-collapsible-editor__helper text-muted small mb-2">{helperText}</p> : null}
                    {renderExecutiveSummaryTextEditor(band, bandPath, editable, patchBandAtPath, textEditorSearchProps, {
                        contentField,
                        optimizeTyping
                    })}
                </div>
            ) : null}
        </div>
    );
}

function GroupSummaryEditor({
    band,
    bandPath,
    editable,
    patchBandAtPath,
    onOpenFullEditor,
    bandFullEditorTooltipId,
    optimizeTyping = false,
    textEditorSearchProps = {}
}) {
    return (
        <CollapsibleTextEditorSection
            title="Group summary"
            defaultOpen={false}
            band={band}
            bandPath={bandPath}
            contentField="value"
            editable={editable}
            patchBandAtPath={patchBandAtPath}
            onOpenFullEditor={onOpenFullEditor}
            bandFullEditorTooltipId={bandFullEditorTooltipId}
            optimizeTyping={optimizeTyping}
            textEditorSearchProps={textEditorSearchProps}
        />
    );
}

function ExecSumBandBuildingCoverage({ count, selectedBuildingCount, onClick, tooltipId }) {
    const tooltip = `${count} of ${selectedBuildingCount} selected buildings have this band — click to view`;
    const label = `${count} matched`;
    return (
        <button
            type="button"
            className="exec-sum-band-building-coverage"
            data-tip={tooltip}
            data-for={tooltipId}
            aria-label={tooltip}
            onClick={e => {
                e.stopPropagation();
                onClick();
            }}
        >
            <i className="fas fa-check-circle exec-sum-band-building-coverage__icon" aria-hidden="true" />
            <span className="exec-sum-band-building-coverage__text">{label}</span>
        </button>
    );
}

function ExecSumBandBuildingsModal({ bandLabel, buildings, onClose }) {
    const [showAll, setShowAll] = useState(false);
    const visibleBuildings = showAll ? buildings : buildings.slice(0, EXEC_SUM_BAND_BUILDINGS_INITIAL_COUNT);
    const hiddenCount = Math.max(buildings.length - EXEC_SUM_BAND_BUILDINGS_INITIAL_COUNT, 0);
    const hasMore = hiddenCount > 0 && !showAll;
    const buildingCountLabel = buildings.length === 1 ? "building" : "buildings";

    return (
        <div className="modal modal-region exec-sum-band-buildings-modal" style={{ display: "block" }} role="dialog" aria-modal="true">
            <div className="modal-dialog exec-sum-band-buildings-modal__dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header exec-sum-band-buildings-modal__header">
                        <div className="exec-sum-band-buildings-modal__heading">
                            <h5 className="modal-title">{bandLabel}</h5>
                            <p className="exec-sum-band-buildings-modal__subtitle">
                                {buildings.length} matching {buildingCountLabel} in your selection
                            </p>
                        </div>
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span aria-hidden="true">
                                <img src="/img/close.svg" alt="" />
                            </span>
                        </button>
                    </div>
                    <div className="modal-body exec-sum-band-buildings-modal__body">
                        {buildings.length ? (
                            <>
                                <p className="exec-sum-band-buildings-modal__summary">
                                    {hasMore
                                        ? `Showing ${EXEC_SUM_BAND_BUILDINGS_INITIAL_COUNT} of ${buildings.length}`
                                        : `${buildings.length} ${buildingCountLabel}`}
                                </p>
                                <div
                                    className={`exec-sum-band-buildings-modal__scroll${
                                        showAll ? " exec-sum-band-buildings-modal__scroll--expanded" : ""
                                    }`}
                                >
                                    <ul className="exec-sum-band-buildings-modal__list">
                                        {visibleBuildings.map((building, index) => (
                                            <li key={building.id || index} className="exec-sum-band-buildings-modal__item notranslate">
                                                <span className="exec-sum-band-buildings-modal__item-icon" aria-hidden="true">
                                                    <i className="fas fa-building" />
                                                </span>
                                                <span className="exec-sum-band-buildings-modal__item-label">
                                                    {formatExecutiveSummaryBandBuildingLabel(building)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {hasMore ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm exec-sum-band-buildings-modal__show-more"
                                        onClick={() => setShowAll(true)}
                                    >
                                        Show all {buildings.length} {buildingCountLabel}
                                    </button>
                                ) : null}
                            </>
                        ) : (
                            <p className="exec-sum-band-buildings-modal__empty">No buildings in your selection have this text band.</p>
                        )}
                    </div>
                    <div className="modal-footer exec-sum-band-buildings-modal__footer">
                        <button type="button" className="btn btn-primary btnRgion" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BandRow({
    band,
    rowKey,
    expanded,
    isImage,
    isComb,
    isText,
    editable,
    onToggleBand,
    onSelectBand,
    barMetaRight,
    rowEndAddon,
    rowLeadingAddon,
    onOpenTextBandFullEditor,
    bandPath,
    canRemove,
    onRequestRemove,
    onRequestRename,
    bandBodyExpanded,
    nestedContent,
    combStickyHeadExtra,
    isSelected,
    searchQuery,
    searchOptions,
    searchActive,
    searchDimmed,
    bandAccordionTooltipId,
    bandFullEditorTooltipId,
    bandRenameTooltipId,
    bandRemoveTooltipId,
    contentLocked = false,
    hideRowFullEditor = false,
    rowTitleAddon = null
}) {
    const rowTypeClass = isComb ? "exec-sum-band-row--comb" : isImage ? "exec-sum-band-row--image" : "exec-sum-band-row--text";
    const accordionTip = expanded ? EXEC_SUM_BAND_ACCORDION_COLLAPSE_TIP : EXEC_SUM_BAND_ACCORDION_EXPAND_TIP;

    const toggleExpanded = e => {
        if (e) e.stopPropagation();
        onToggleBand && onToggleBand(rowKey);
    };

    const handleBarActivate = e => {
        if (e) e.stopPropagation();
        onSelectBand && onSelectBand(rowKey);
        toggleExpanded(e);
    };

    const handleBarKeyDown = e => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBarActivate(e);
        }
    };

    const rowClassName = [
        "exec-sum-band-row",
        rowTypeClass,
        expanded ? "exec-sum-band-row--expanded" : "",
        isSelected ? "exec-sum-band-row--selected" : "",
        searchDimmed ? "exec-sum-band-row--search-dim" : "",
        searchActive ? "exec-sum-band-row--search-active" : "",
        contentLocked ? "exec-sum-band-row--content-locked" : ""
    ]
        .filter(Boolean)
        .join(" ");

    const barInner = (
        <>
            <div className="exec-sum-band-row__main">
                <BandRowTypeIcon isComb={isComb} isImage={isImage} />
                <div className="exec-sum-band-row__title-block exec-sum-band-row__title-block--single-line">
                    <div className="exec-sum-band-row__title-line">
                        <strong
                            className="exec-sum-band-row__title exec-sum-band-row__title--clickable"
                            onClick={handleBarActivate}
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleBarActivate(e);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                        >
                            {highlightExecSumSearchLabel(getExecutiveSummaryBandLabel(band), searchQuery, searchOptions)}
                        </strong>
                        {editable && onRequestRename ? (
                            <button
                                type="button"
                                className="exec-sum-band-row__icon-btn exec-sum-band-row__rename-btn"
                                onClick={e => {
                                    e.stopPropagation();
                                    onRequestRename(bandPath);
                                }}
                                aria-label={EXEC_SUM_BAND_RENAME_TIP}
                                data-tip={EXEC_SUM_BAND_RENAME_TIP}
                                data-for={bandRenameTooltipId}
                            >
                                <i className="fas fa-pencil-alt" aria-hidden="true" />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="exec-sum-band-row__icon-btn exec-sum-band-row__accordion-btn"
                            onClick={handleBarActivate}
                            aria-label={accordionTip}
                            aria-expanded={expanded}
                            data-tip={accordionTip}
                            data-for={bandAccordionTooltipId}
                        >
                            <i className={`fas ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true" />
                        </button>
                        {rowTitleAddon}
                    </div>
                </div>
                {barMetaRight ? (
                    <div className="exec-sum-band-row__bar-meta text-muted small">{barMetaRight}</div>
                ) : null}
            </div>
            {rowLeadingAddon ? (
                <div className="exec-sum-band-row__leading-addons" role="presentation">
                    {rowLeadingAddon}
                </div>
            ) : null}
            <div className="exec-sum-band-row__end" role="presentation">
                {rowEndAddon}
                {isText && onOpenTextBandFullEditor && !hideRowFullEditor ? (
                    <button
                        type="button"
                        className="exec-sum-band-row__icon-btn"
                        onClick={e => {
                            e.stopPropagation();
                            onOpenTextBandFullEditor(bandPath);
                        }}
                        aria-label={EXEC_SUM_BAND_FULL_EDITOR_TIP}
                        data-tip={EXEC_SUM_BAND_FULL_EDITOR_TIP}
                        data-for={bandFullEditorTooltipId}
                    >
                        <img src="/img/expand1.svg" alt="" width="18" height="18" />
                    </button>
                ) : (
                    <span className="exec-sum-band-row__icon-slot" aria-hidden="true" />
                )}
                {editable && canRemove ? (
                    <button
                        type="button"
                        className="exec-sum-band-row__icon-btn exec-sum-band-row__icon-btn--danger"
                        onClick={e => {
                            e.stopPropagation();
                            onRequestRemove();
                        }}
                        aria-label={EXEC_SUM_BAND_REMOVE_TIP}
                        data-tip={EXEC_SUM_BAND_REMOVE_TIP}
                        data-for={bandRemoveTooltipId}
                    >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                ) : null}
            </div>
        </>
    );

    const barEl = (
        <div
            className={`exec-sum-band-row__bar exec-sum-band-row__bar--clickable${
                rowEndAddon ? " exec-sum-band-row__bar--has-end-addon" : ""
            }${rowLeadingAddon ? " exec-sum-band-row__bar--has-leading-addon" : ""}`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            onClick={handleBarActivate}
            onKeyDown={handleBarKeyDown}
        >
            {barInner}
        </div>
    );

    const useCombStickyHead = isComb && expanded;

    return (
        <li className={rowClassName} id={getBandRowDomId(rowKey)}>
            <div className="exec-sum-band-row__drag-shell">
                {useCombStickyHead ? (
                    <div className="exec-sum-comb-sticky-head">
                        {barEl}
                        {combStickyHeadExtra}
                    </div>
                ) : (
                    barEl
                )}
                {expanded ? (
                    <div className="exec-sum-band-body">
                        {bandBodyExpanded}
                        {nestedContent}
                    </div>
                ) : null}
            </div>
        </li>
    );
}

function LeafBandRow({
    band,
    bandPath,
    expandedBandKeys,
    editable,
    imageMap,
    allowImageAssignment,
    onToggleBand,
    onOpenTextBandFullEditor,
    onOpenImageLayoutEditor,
    patchBandAtPath,
    canRemoveBand,
    setPendingRemoveBandPath,
    setPendingRemoveImageSlot,
    onRequestRename,
    onRequestGalleryPick,
    onRequestBuildingPick,
    openBuildingPick,
    getBandSearchProps,
    getTextEditorSearchProps,
    getEditorWrapperClassName,
    activeSearchMatch,
    debouncedSearchQuery,
    onShowBandBuildings,
    selectedBuildingCount = 0,
    buildingCoverageTooltipId,
    bandAccordionTooltipId,
    bandFullEditorTooltipId,
    bandRenameTooltipId,
    bandRemoveTooltipId,
    optimizeTyping = false,
    useSimpleReadOnly = false,
    showSampleTextEditor = false,
    onOpenSampleTextBandFullEditor,
    showSampleTextViewer = false,
    onOpenSampleTextViewer,
    bandSampleTextTooltipId,
    activeSampleTextViewerRowKey,
    showBandQaStatus = false,
    canEditBandQaStatus = false,
    canMarkBandQaPassed = false,
    onBandQaStatusChange,
    renderBandLeadingAddon
}) {
    const rowKey = bandPathToRowKey(bandPath, band);
    const { searchQuery, searchOptions, searchActive, searchDimmed, isSelected, onSelectBand } = getBandSearchProps(rowKey);

    const expanded = !!expandedBandKeys[rowKey];
    const isImage = isExecutiveSummaryImageBand(band);
    const isText = !isImage;
    const bandStableIdForSlots = sanitizeRbdId(rowKey);
    const textEditorSearchProps = getTextEditorSearchProps(band, rowKey);
    const captionHighlight =
        activeSearchMatch?.rowKey === rowKey && activeSearchMatch?.field === "caption" ? activeSearchMatch : null;

    const contentLocked = isExecutiveSummaryBandContentLocked(band);
    const canEditBandContent = editable && !contentLocked;
    const useTemplateSampleTextLayout = showSampleTextEditor && isExecutiveSummarySampleTextBand(band);

    const bandBuildings = getExecutiveSummaryBandBuildings(band);
    const buildingCoverageMeta =
        onShowBandBuildings && isText && Array.isArray(band.buildings) ? (
            <ExecSumBandBuildingCoverage
                count={bandBuildings.length}
                selectedBuildingCount={selectedBuildingCount}
                tooltipId={buildingCoverageTooltipId}
                onClick={() => onShowBandBuildings(band)}
            />
        ) : null;

    const bandBodyExpanded = expanded
        ? isImage
            ? (
                  <>
                      <div className="exec-sum-image-layout-row mb-3">
                          <div className="exec-sum-image-layout-label d-flex flex-wrap align-items-center gap-1">
                              <span className="text-muted small mb-0">Layout: </span>
                              <strong className="text-dark small mb-0">{formatExecutiveImageBandLayoutOnly(band)}</strong>
                              {canEditBandContent && onOpenImageLayoutEditor ? (
                                  <button
                                      type="button"
                                      className="exec-sum-band-row__icon-btn exec-sum-image-layout-row__edit"
                                      onClick={e => {
                                          e.stopPropagation();
                                          onOpenImageLayoutEditor(bandPath);
                                      }}
                                      aria-label="Change layout"
                                      title="Change layout"
                                  >
                                      <i className="fas fa-pencil-alt" aria-hidden="true" />
                                  </button>
                              ) : null}
                          </div>
                      </div>
                      {allowImageAssignment ? (
                          <>
                              <ImageBandSlotGrid
                                  band={band}
                                  bandPath={bandPath}
                                  imageMap={imageMap}
                                  editable={canEditBandContent}
                                  onUpdateBand={patchBandAtPath}
                                  bandStableIdForSlots={bandStableIdForSlots}
                                  onRequestRemoveSlot={
                                      canEditBandContent
                                          ? slotIndex => setPendingRemoveImageSlot({ bandPath, slotIndex })
                                          : undefined
                                  }
                                  searchQuery={captionHighlight ? debouncedSearchQuery : ""}
                                  searchOptions={searchOptions}
                                  captionHighlightSlotIndex={captionHighlight?.captionSlotIndex ?? null}
                              />
                              {canEditBandContent ? (
                                  <div className="mt-2">
                                      {onRequestBuildingPick ? (
                                          <button
                                              type="button"
                                              className="btn btn-outline-secondary btn-sm mr-2"
                                              onClick={() => openBuildingPick(bandPath)}
                                          >
                                              Assign from building
                                          </button>
                                      ) : null}
                                      {onRequestGalleryPick ? (
                                          <button
                                              type="button"
                                              className="btn btn-outline-primary btn-sm"
                                              onClick={() => onRequestGalleryPick(bandPath)}
                                          >
                                              Assign from gallery
                                          </button>
                                      ) : null}
                                      {!onRequestBuildingPick && !onRequestGalleryPick ? (
                                          <p className="text-muted small mb-0">
                                              Save the building first to assign images to this band.
                                          </p>
                                      ) : null}
                                  </div>
                              ) : contentLocked ? (
                                  <p className="text-muted small mb-0 mt-2">
                                      Image assignment is locked while this band is Ready for QA or QA Passed.
                                  </p>
                              ) : null}
                          </>
                      ) : (
                          <p className="text-muted small mb-0">
                              Assign images when editing an individual building.
                          </p>
                      )}
                  </>
              )
            : useSimpleReadOnly ? (
                  <ExecutiveSummaryLogCompareTextPreview value={band.value} />
              ) : useTemplateSampleTextLayout ? (
                  <div className="exec-sum-template-text-sections">
                      <CollapsibleTextEditorSection
                          title="Content"
                          defaultOpen
                          band={band}
                          bandPath={bandPath}
                          contentField="value"
                          editable={canEditBandContent}
                          patchBandAtPath={patchBandAtPath}
                          onOpenFullEditor={onOpenTextBandFullEditor}
                          bandFullEditorTooltipId={bandFullEditorTooltipId}
                          optimizeTyping={optimizeTyping}
                          textEditorSearchProps={textEditorSearchProps}
                      />
                      <CollapsibleTextEditorSection
                          title="Sample text"
                          helperText="Reference content for building users after sync."
                          defaultOpen={false}
                          band={band}
                          bandPath={bandPath}
                          contentField="sample_text"
                          editable={canEditBandContent}
                          patchBandAtPath={patchBandAtPath}
                          onOpenFullEditor={onOpenSampleTextBandFullEditor}
                          bandFullEditorTooltipId={bandFullEditorTooltipId}
                          fullEditorTip={EXEC_SUM_BAND_SAMPLE_TEXT_FULL_EDITOR_TIP}
                          optimizeTyping={optimizeTyping}
                          textEditorSearchProps={textEditorSearchProps}
                      />
                  </div>
              ) : (
                  <ExecutiveSummaryTextEditor
                      band={band}
                      bandPath={bandPath}
                      editable={canEditBandContent}
                      patchBandAtPath={patchBandAtPath}
                      wrapperClassName={getEditorWrapperClassName ? getEditorWrapperClassName(bandPath, band) : ""}
                      optimizeTyping={optimizeTyping}
                      {...textEditorSearchProps}
                  />
              )
        : null;

    const lockedContentNotice =
        contentLocked && expanded && editable ? (
            <p className="exec-sum-band-content-locked-note text-muted small mb-2">
                Content editing is locked while this band is Ready for QA or QA Passed.
            </p>
        ) : null;

    const bandBodyWithNotice = bandBodyExpanded ? (
        <>
            {lockedContentNotice}
            {bandBodyExpanded}
        </>
    ) : null;

    const qaStatusControl = showBandQaStatus ? (
            <ExecutiveSummaryBandQaStatus
                band={band}
                canEditBandQaStatus={canEditBandQaStatus}
                canMarkBandQaPassed={canMarkBandQaPassed}
                onStatusChange={
                    onBandQaStatusChange ? nextStatus => onBandQaStatusChange(bandPath, nextStatus) : undefined
                }
            />
        ) : null;

    const rowEndAddons = buildingCoverageMeta ? (
        <div className="exec-sum-band-row__end-addons">{buildingCoverageMeta}</div>
    ) : null;

    const sampleTextTitleAddon =
        showSampleTextViewer && isText && isExecutiveSummarySampleTextBand(band) ? (
            <button
                type="button"
                className={`exec-sum-band-row__icon-btn exec-sum-band-row__sample-text-btn${
                    activeSampleTextViewerRowKey === rowKey ? " exec-sum-band-row__sample-text-btn--active" : ""
                }`}
                onClick={e => {
                    e.stopPropagation();
                    if (typeof onOpenSampleTextViewer === "function") {
                        onOpenSampleTextViewer(band, bandPath, rowKey);
                    }
                }}
                aria-label={EXEC_SUM_BAND_SAMPLE_TEXT_VIEW_TIP}
                data-tip={EXEC_SUM_BAND_SAMPLE_TEXT_VIEW_TIP}
                data-for={bandSampleTextTooltipId}
            >
                <i className="fas fa-file-alt" aria-hidden="true" />
            </button>
        ) : null;

    return (
        <BandRow
            band={band}
            rowKey={rowKey}
            bandPath={bandPath}
            expanded={expanded}
            isImage={isImage}
            isComb={false}
            isText={isText}
            editable={editable}
            onToggleBand={onToggleBand}
            onSelectBand={onSelectBand}
            onOpenTextBandFullEditor={onOpenTextBandFullEditor}
            barMetaRight={isImage ? formatExecutiveImageBandSubheading(band) : null}
            rowLeadingAddon={
                (renderBandLeadingAddon && renderBandLeadingAddon(band, bandPath)) || qaStatusControl
            }
            rowEndAddon={rowEndAddons}
            canRemove={canRemoveBand(band)}
            onRequestRemove={() => setPendingRemoveBandPath(bandPath)}
            onRequestRename={onRequestRename && !isExecutiveSummaryTradeBand(band) ? onRequestRename : undefined}
            bandAccordionTooltipId={bandAccordionTooltipId}
            bandFullEditorTooltipId={bandFullEditorTooltipId}
            bandRenameTooltipId={bandRenameTooltipId}
            bandRemoveTooltipId={bandRemoveTooltipId}
            bandBodyExpanded={bandBodyWithNotice}
            rowTitleAddon={sampleTextTitleAddon}
            contentLocked={contentLocked}
            hideRowFullEditor={useTemplateSampleTextLayout}
            searchQuery={searchQuery}
            searchOptions={searchOptions}
            searchActive={searchActive}
            searchDimmed={searchDimmed}
            isSelected={isSelected}
        />
    );
}

function CombBandRow({
    band,
    bandPath,
    expandedBandKeys,
    editable,
    onToggleBand,
    onSelectBand,
    onOpenAddBandChooser,
    canRemoveBand,
    setPendingRemoveBandPath,
    patchBandAtPath,
    onOpenTextBandFullEditor,
    leafBandRowProps,
    renderBandLeadingAddon,
    onRequestRename,
    onToggleGroupChildren,
    isSelected,
    searchQuery,
    searchOptions,
    searchActive,
    searchDimmed,
    debouncedSearchQuery,
    activeSearchMatch,
    getTextEditorSearchProps
}) {
    const rowKey = bandPathToRowKey(bandPath, band);
    const expanded = !!expandedBandKeys[rowKey];
    const children = getCombBandChildren(band);
    const barMetaRight = formatCombBandMeta(children);
    const childRowKeys = useMemo(
        () => children.map((child, ci) => bandPathToRowKey([bandPath[0], ci], child)).filter(Boolean),
        [children, bandPath]
    );
    const anyChildExpanded = childRowKeys.some(key => !!expandedBandKeys[key]);

    const { bandAccordionTooltipId, bandFullEditorTooltipId, bandRenameTooltipId, bandRemoveTooltipId, optimizeTyping } =
        leafBandRowProps;

    const summaryBody = expanded ? (
        <GroupSummaryEditor
            band={band}
            bandPath={bandPath}
            editable={editable}
            patchBandAtPath={patchBandAtPath}
            onOpenFullEditor={onOpenTextBandFullEditor}
            bandFullEditorTooltipId={bandFullEditorTooltipId}
            optimizeTyping={optimizeTyping}
            textEditorSearchProps={getTextEditorSearchProps(band, rowKey)}
        />
    ) : null;

    const nestedContent =
        expanded && children.length > 0 ? (
            <ul className="exec-sum-band-list exec-sum-band-list--nested">
                {children.map((child, ci) => (
                    <LeafBandRow
                        key={bandPathToRowKey([bandPath[0], ci], child)}
                        band={child}
                        bandPath={[bandPath[0], ci]}
                        expandedBandKeys={expandedBandKeys}
                        {...leafBandRowProps}
                    />
                ))}
            </ul>
        ) : null;

    const groupStickyActions =
        expanded &&
        !leafBandRowProps.isLogCompare &&
        (childRowKeys.length > 0 || (editable && onOpenAddBandChooser)) ? (
            <div className="exec-sum-comb-add-band">
                {editable && onOpenAddBandChooser ? (
                    <button
                        type="button"
                        className="exec-sum-toolbar-btn exec-sum-toolbar-btn--outline btn-sm"
                        onClick={e => {
                            e.stopPropagation();
                            onOpenAddBandChooser(bandPath);
                        }}
                    >
                        + Add band
                    </button>
                ) : null}
                {childRowKeys.length > 0 ? (
                    <button
                        type="button"
                        className="exec-sum-toolbar-btn exec-sum-toolbar-btn--ghost btn-sm"
                        aria-label={anyChildExpanded ? EXEC_SUM_GROUP_COLLAPSE_BANDS_TIP : EXEC_SUM_GROUP_EXPAND_BANDS_TIP}
                        data-tip={anyChildExpanded ? EXEC_SUM_GROUP_COLLAPSE_BANDS_TIP : EXEC_SUM_GROUP_EXPAND_BANDS_TIP}
                        data-for={bandAccordionTooltipId}
                        onClick={e => {
                            e.stopPropagation();
                            if (typeof onToggleGroupChildren === "function") {
                                onToggleGroupChildren(childRowKeys, anyChildExpanded);
                            }
                        }}
                    >
                        {renderExpandCollapseIcon(anyChildExpanded)}
                        {anyChildExpanded ? "Collapse bands" : "Expand bands"}
                    </button>
                ) : null}
            </div>
        ) : null;

    return (
        <BandRow
            key={rowKey}
            band={band}
            rowKey={rowKey}
            bandPath={bandPath}
            expanded={expanded}
            isImage={false}
            isComb
            isText={false}
            editable={editable}
            onToggleBand={onToggleBand}
            onSelectBand={onSelectBand}
            barMetaRight={barMetaRight}
            canRemove={canRemoveBand(band)}
            onRequestRemove={() => setPendingRemoveBandPath(bandPath)}
            onRequestRename={onRequestRename}
            bandAccordionTooltipId={bandAccordionTooltipId}
            bandRenameTooltipId={bandRenameTooltipId}
            bandRemoveTooltipId={bandRemoveTooltipId}
            combStickyHeadExtra={groupStickyActions}
            rowLeadingAddon={renderBandLeadingAddon && renderBandLeadingAddon(band, bandPath)}
            isSelected={isSelected}
            searchQuery={searchQuery}
            searchOptions={searchOptions}
            searchActive={searchActive}
            searchDimmed={searchDimmed}
            bandBodyExpanded={summaryBody}
            nestedContent={nestedContent}
        />
    );
}

function BuildingImagePickModal({ buildingImages, currentIds, loading = false, onPick, onClose }) {
    const sorted = useMemo(() => (Array.isArray(buildingImages) ? [...buildingImages] : []), [buildingImages]);
    const bandIdSet = useMemo(() => new Set((currentIds || []).map(String)), [currentIds]);
    const lockedIds = useMemo(() => {
        const locked = new Set();
        sorted.forEach(img => {
            const tid = getBuildingImageRecordId(img);
            if (!tid) return;
            const keys = collectBuildingImageLookupIds(img);
            if (keys.some(k => bandIdSet.has(String(k)))) locked.add(tid);
        });
        return locked;
    }, [sorted, bandIdSet]);
    const [local, setLocal] = useState(() => new Set());
    const maxImages = EXEC_SUMMARY_IMAGE_BAND_MAX_IMAGES;

    useEffect(() => {
        const next = new Set();
        sorted.forEach(img => {
            const tid = getBuildingImageRecordId(img);
            if (!tid) return;
            const keys = collectBuildingImageLookupIds(img);
            if (keys.some(k => bandIdSet.has(String(k)))) next.add(tid);
        });
        setLocal(next);
    }, [sorted, bandIdSet]);

    const toggle = id => {
        if (lockedIds.has(id)) return;
        setLocal(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else if (n.size < maxImages) n.add(id);
            else showAlert(`You can add up to ${maxImages} images per band.`);
            return n;
        });
    };

    const apply = () => {
        onPick([...local]);
        onClose();
    };

    return (
        <div className="modal modal-region modal-view inbox-modal" style={{ display: "block" }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Building images</h5>
                        <button type="button" className="close" onClick={onClose} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <div className="exec-sum-pick-modal-loader">
                                <Loader />
                                <p className="small text-muted mb-0 mt-2">Loading building images…</p>
                            </div>
                        ) : (
                            <>
                                <p className="small text-muted">
                                    Select images for this band. {local.size} selected (max {maxImages}).
                                </p>
                                {sorted.length === 0 ? (
                                    <p className="text-muted mb-0">No building images found.</p>
                                ) : (
                                    <div className="row exec-sum-pick-modal-grid">
                                        {sorted.map(img => {
                                            const id = getBuildingImageRecordId(img);
                                            if (!id) return null;
                                            const locked = lockedIds.has(id);
                                            const on = local.has(id);
                                            const thumb = resolveExecutiveSummaryImageSrc(img) || THUMB_PH;
                                            return (
                                                <div key={id} className="col-6 col-md-4 mb-2">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-light border w-100 p-1 exec-sum-pick-tile${on ? " exec-sum-pick-tile--on" : ""}${
                                                            locked ? " exec-sum-pick-tile--locked" : ""
                                                        }`}
                                                        onClick={() => toggle(id)}
                                                        aria-disabled={locked}
                                                        aria-label={
                                                            locked ? "Image assigned to band (cannot deselect here)" : undefined
                                                        }
                                                    >
                                                        <label
                                                            className={`container-check exec-sum-pick-tile__check${
                                                                locked ? " container-check--disabled" : ""
                                                            }`}
                                                        >
                                                            <input type="checkbox" checked={on} disabled={locked} readOnly tabIndex={-1} />
                                                            <span className="checkmark" />
                                                        </label>
                                                        <img src={thumb} alt="" className="w-100" style={{ height: 80, objectFit: "cover" }} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={apply} disabled={loading}>
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function ExecutiveSummaryBandsPanel({
    bands = [],
    buildingImages = [],
    expandedBandKeys = {},
    onToggleBand,
    onExpandBands,
    onCollapseBands,
    editable = false,
    onBandsChange,
    onSave,
    onDiscard,
    saving = false,
    dirty = false,
    discardDirty,
    onOpenAddBandChooser,
    onRequestSyncFromTemplate,
    syncingFromTemplate = false,
    onRequestGalleryPick,
    onRequestBuildingPick,
    onOpenTextBandFullEditor,
    onOpenSampleTextBandFullEditor,
    onOpenImageLayoutEditor,
    canRemoveBand = () => true,
    allowImageAssignment = true,
    variant = "default",
    multiselectClass,
    tooltipIdPrefix = "",
    selectedBuildingCount = 0,
    showBandQaStatus = true,
    showSampleTextEditor = false,
    canEditBandQaStatus = false,
    canMarkBandQaPassed = false,
    renderBandLeadingAddon
}) {
    const isMultiEdit = variant === "multiEdit";
    const isLogCompare = variant === "logCompare";
    const effectiveShowBandQaStatus = showBandQaStatus && !isLogCompare;
    const [bandBuildingsModal, setBandBuildingsModal] = useState(null);
    const tooltipScope = String(tooltipIdPrefix ?? "").trim();
    const searchMatchCaseTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_search_match_case`
        : "exec_sum_search_match_case";
    const searchWholeWordTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_search_whole_word`
        : "exec_sum_search_whole_word";
    const syncTemplateTooltipId = tooltipScope
        ? `${tooltipScope}_sync_executive_summary_template`
        : "sync_executive_summary_template";
    const buildingCoverageTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_band_building_coverage`
        : "exec_sum_band_building_coverage";
    const bandAccordionTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_band_accordion`
        : "exec_sum_band_accordion";
    const bandFullEditorTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_band_full_editor`
        : "exec_sum_band_full_editor";
    const bulkExpandBandsTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_bulk_expand_bands`
        : "exec_sum_bulk_expand_bands";
    const bandRenameTooltipId = tooltipScope ? `${tooltipScope}_exec_sum_band_rename` : "exec_sum_band_rename";
    const bandRemoveTooltipId = tooltipScope ? `${tooltipScope}_exec_sum_band_remove` : "exec_sum_band_remove";
    const bandSampleTextTooltipId = tooltipScope
        ? `${tooltipScope}_exec_sum_band_sample_text`
        : "exec_sum_band_sample_text";
    const effectiveDiscardDirty = discardDirty !== undefined ? discardDirty : dirty;
    const bandBuildingCountsKey = (bands || [])
        .map(band => (Array.isArray(band?.buildings) ? band.buildings.length : 0))
        .join(",");
    const imageMap = useMemo(() => buildBuildingImageMap(buildingImages), [buildingImages]);
    const panelRef = useRef(null);
    const panelHeadRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchAreaRef = useRef(null);
    const lastSearchNavRef = useRef({ rowKey: null, field: null });
    const [searchQuery, setSearchQuery] = useState("");
    const [searchMatchCase, setSearchMatchCase] = useState(false);
    const [searchWholeWord, setSearchWholeWord] = useState(false);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [activeSearchIndex, setActiveSearchIndex] = useState(0);
    const [sampleTextViewer, setSampleTextViewer] = useState(null);
    const [selectedBandKey, setSelectedBandKey] = useState(null);
    const [buildingPickCtx, setBuildingPickCtx] = useState(null);
    const [buildingPickImagesLoading, setBuildingPickImagesLoading] = useState(false);
    const [pendingRemoveBandPath, setPendingRemoveBandPath] = useState(null);
    const [pendingRemoveImageSlot, setPendingRemoveImageSlot] = useState(null);
    const [pendingDiscardConfirm, setPendingDiscardConfirm] = useState(false);
    const [organizeModalOpen, setOrganizeModalOpen] = useState(false);
    const [renameBandCtx, setRenameBandCtx] = useState(null);
    const showAddBand = !isMultiEdit && editable && onOpenAddBandChooser;
    const showSyncFromTemplate = !isMultiEdit && editable && typeof onRequestSyncFromTemplate === "function";
    const showOrganizeBands = !isMultiEdit && editable && typeof onBandsChange === "function" && bands.length > 0;
    const qaBreakdown = useMemo(() => getExecutiveSummaryQaStatusBreakdown(bands), [bands]);
    const effectiveCanRemoveBand = isMultiEdit ? () => false : canRemoveBand;
    const { bulkBandsBusy, toggleBandRowKeys } = useExecutiveSummaryBulkBandsToggle(
        bands,
        expandedBandKeys,
        onExpandBands,
        onCollapseBands
    );

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), EXEC_SUM_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const searchFindOptions = useMemo(
        () => ({ matchCase: searchMatchCase, wholeWord: searchWholeWord }),
        [searchMatchCase, searchWholeWord]
    );

    const searchMatches = useMemo(
        () =>
            buildExecutiveSummarySearchMatches(bands, debouncedSearchQuery, {
                imageMap,
                ...searchFindOptions
            }),
        [bands, debouncedSearchQuery, imageMap, searchFindOptions]
    );

    const activeSearchMatch = searchMatches[activeSearchIndex] ?? null;

    /** All matches plus ancestor groups stay visible; non-matches are dimmed. */
    const searchVisibleRowKeys = useMemo(
        () => new Set(collectExecutiveSummarySearchExpandKeys(bands, searchMatches)),
        [bands, searchMatches]
    );

    useEffect(() => {
        setActiveSearchIndex(0);
        lastSearchNavRef.current = { rowKey: null, field: null };
    }, [debouncedSearchQuery, searchMatchCase, searchWholeWord]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [
        bands?.length,
        bandBuildingCountsKey,
        searchMatchCase,
        searchWholeWord,
        tooltipIdPrefix,
        selectedBuildingCount,
        expandedBandKeys,
        bulkBandsBusy
    ]);

    useEffect(() => {
        if (!debouncedSearchQuery || !searchMatches.length) return;
        const match = searchMatches[activeSearchIndex];
        if (!match) return;

        const prevNav = lastSearchNavRef.current;
        const sameBand = prevNav.rowKey === match.rowKey;
        const sameField = prevNav.field === match.field;
        lastSearchNavRef.current = { rowKey: match.rowKey, field: match.field };

        setSelectedBandKey(match.rowKey);

        if (!sameBand && onExpandBands) {
            onExpandBands(collectExecutiveSummarySearchExpandKeys(bands, [match]));
        }

        if (sameBand && sameField && match.field === "body") {
            return;
        }

        const matchedBand = getBandAtPath(bands, match.bandPath);
        const preferSummary =
            isExecutiveSummaryCombBand(matchedBand) && match.field === "body";
        const scrollDelay = sameBand ? 0 : 50;
        const timeoutId = window.setTimeout(() => {
            scrollExecutiveSummaryBandIntoView(match.rowKey, panelHeadRef.current, { preferSummary });
        }, scrollDelay);
        return () => window.clearTimeout(timeoutId);
    }, [debouncedSearchQuery, searchMatches, activeSearchIndex, bands, onExpandBands]);

    useEffect(() => {
        const scope = panelRef.current;
        const head = panelHeadRef.current;
        if (!scope || !head) return undefined;

        const updateStickyTop = () => {
            scope.style.setProperty("--exec-sum-sticky-top", `${head.offsetHeight}px`);
        };

        updateStickyTop();
        const observer = new ResizeObserver(updateStickyTop);
        observer.observe(head);
        return () => observer.disconnect();
    }, [bands.length]);

    const goToSearchMatch = useCallback(
        delta => {
            if (!searchMatches.length) return;
            setActiveSearchIndex(prev => (prev + delta + searchMatches.length) % searchMatches.length);
        },
        [searchMatches]
    );

    const resetSearchOptions = useCallback(() => {
        setSearchMatchCase(false);
        setSearchWholeWord(false);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery("");
        setDebouncedSearchQuery("");
        setActiveSearchIndex(0);
        resetSearchOptions();
    }, [resetSearchOptions]);

    const handleSearchBlur = useCallback(
        e => {
            if (searchAreaRef.current?.contains(e.relatedTarget)) return;
            if (!searchQuery.trim()) {
                resetSearchOptions();
            }
        },
        [searchQuery, resetSearchOptions]
    );

    const searchOpen = !!(searchQuery.trim() || debouncedSearchQuery);

    const getTextEditorSearchProps = useCallback(
        (band, rowKey) => {
            if (!debouncedSearchQuery || !searchVisibleRowKeys.has(rowKey)) {
                return {
                    searchHighlightQuery: "",
                    searchHighlightOccurrenceIndex: 0,
                    isSearchHighlightActive: false,
                    openSummaryForSearch: false,
                    searchFindOptions
                };
            }
            const bodyText = stripExecutiveSummaryHtml(band?.value ?? "");
            const hasBodyMatch = textMatchesExecSumSearch(bodyText, debouncedSearchQuery, searchFindOptions);
            const isActiveBody =
                activeSearchMatch?.rowKey === rowKey && activeSearchMatch?.field === "body";

            return {
                searchHighlightQuery: debouncedSearchQuery,
                searchHighlightOccurrenceIndex: isActiveBody ? activeSearchMatch.occurrenceIndex : 0,
                isSearchHighlightActive: isActiveBody,
                openSummaryForSearch: hasBodyMatch,
                searchFindOptions
            };
        },
        [debouncedSearchQuery, searchVisibleRowKeys, activeSearchMatch, searchFindOptions]
    );

    const getBandSearchProps = useCallback(
        rowKey => ({
            searchQuery: debouncedSearchQuery,
            searchOptions: searchFindOptions,
            searchDimmed: !!debouncedSearchQuery && !searchVisibleRowKeys.has(rowKey),
            searchActive: activeSearchMatch?.rowKey === rowKey,
            isSelected: selectedBandKey === rowKey,
            onSelectBand: setSelectedBandKey
        }),
        [debouncedSearchQuery, searchFindOptions, searchVisibleRowKeys, activeSearchMatch, selectedBandKey]
    );

    const patchBandAtPath = useCallback(
        (bandPath, partial) => {
            if (!onBandsChange) return;
            const band = getBandAtPath(bands, bandPath);
            if (isExecutiveSummaryBandContentLocked(band) && isExecutiveSummaryBandContentPatch(partial)) return;
            onBandsChange(updateBandAtPath(bands, bandPath, partial));
        },
        [bands, onBandsChange]
    );

    const removeBandAt = useCallback(
        bandPath => {
            if (!onBandsChange) return;
            onBandsChange(removeBandAtPath(bands, bandPath));
        },
        [bands, onBandsChange]
    );

    const openRenameBand = useCallback(
        bandPath => {
            const band = getBandAtPath(bands, bandPath);
            if (!band || isExecutiveSummaryTradeBand(band)) return;
            setRenameBandCtx({
                bandPath,
                isImageBand: isExecutiveSummaryImageBand(band),
                isGroupBand: isExecutiveSummaryCombBand(band)
            });
        },
        [bands]
    );

    const buildingPickRequestRef = useRef(0);

    const openBuildingPick = useCallback(
        bandPath => {
            const band = bands[bandPath[0]];
            const target =
                bandPath.length === 2 && isExecutiveSummaryCombBand(band)
                    ? getCombBandChildren(band)[bandPath[1]]
                    : band;
            if (!target || !isExecutiveSummaryImageBand(target) || isExecutiveSummaryBandContentLocked(target)) return;
            const raw = getExecSummaryImageData(target);
            const selectedIds = raw.map(r => String(r.id));
            setBuildingPickCtx({ bandPath, selectedIds });
            if (onRequestBuildingPick) {
                const requestId = ++buildingPickRequestRef.current;
                setBuildingPickImagesLoading(true);
                onRequestBuildingPick()
                    .catch(() => {
                        if (buildingPickRequestRef.current !== requestId) return;
                        setBuildingPickCtx(null);
                        showAlert("Could not load building images. Please try again.");
                    })
                    .finally(() => {
                        if (buildingPickRequestRef.current === requestId) {
                            setBuildingPickImagesLoading(false);
                        }
                    });
            }
        },
        [bands, onRequestBuildingPick]
    );

    const applyBuildingPick = selectedIdList => {
        if (!buildingPickCtx || !onBandsChange) {
            setBuildingPickCtx(null);
            return;
        }
        const { bandPath } = buildingPickCtx;
        const parent = bands[bandPath[0]];
        const band =
            bandPath.length === 2 && isExecutiveSummaryCombBand(parent)
                ? getCombBandChildren(parent)[bandPath[1]]
                : parent;
        if (!band || !isExecutiveSummaryImageBand(band)) {
            setBuildingPickCtx(null);
            return;
        }
        const layout = getExecSummaryImageLayout(band);
        const ids = selectedIdList.slice(0, EXEC_SUMMARY_IMAGE_BAND_MAX_IMAGES).map(String);
        const data = mergeExecutiveSummaryBuildingPickSlots(getExecSummaryImageData(band), ids, buildingImages);
        patchBandAtPath(bandPath, {
            type: "image_band",
            data: clampExecutiveSummaryImageData(data),
            config: { ...(band.config && typeof band.config === "object" ? band.config : {}), type: layout },
            band_kind: undefined,
            image_layout: undefined,
            images: undefined
        });
        setBuildingPickCtx(null);
    };

    const removeBandConfirm = useMemo(
        () =>
            pendingRemoveBandPath != null
                ? getExecutiveSummaryRemoveBandConfirmMessage(bands, pendingRemoveBandPath, { multiEdit: isMultiEdit })
                : null,
        [bands, pendingRemoveBandPath, isMultiEdit]
    );

    const confirmRemoveBand = () => {
        if (!pendingRemoveBandPath) return;
        removeBandAt(pendingRemoveBandPath);
        setPendingRemoveBandPath(null);
    };

    const applyBandOrder = async nextBands => {
        if (onBandsChange) onBandsChange(nextBands);
        if (onSave) {
            await onSave(nextBands);
        }
        setOrganizeModalOpen(false);
    };

    const confirmRemoveImageSlot = () => {
        if (!pendingRemoveImageSlot || !onBandsChange) {
            setPendingRemoveImageSlot(null);
            return;
        }
        const { bandPath, slotIndex } = pendingRemoveImageSlot;
        const parent = bands[bandPath[0]];
        const band =
            bandPath.length === 2 && isExecutiveSummaryCombBand(parent)
                ? getCombBandChildren(parent)[bandPath[1]]
                : parent;
        if (!band || !isExecutiveSummaryImageBand(band)) {
            setPendingRemoveImageSlot(null);
            return;
        }
        const layout = getExecSummaryImageLayout(band);
        const raw = getExecSummaryImageData(band);
        const data = normalizeExecSummaryImageDataForEditor(raw.filter((_, j) => j !== slotIndex));
        patchBandAtPath(bandPath, {
            type: "image_band",
            data: clampExecutiveSummaryImageData(data),
            config: { ...(band.config && typeof band.config === "object" ? band.config : {}), type: layout },
            band_kind: undefined,
            image_layout: undefined,
            images: undefined
        });
        setPendingRemoveImageSlot(null);
    };

    const getEditorWrapperClassName = useCallback(
        (bandPath, band) => {
            if (!isMultiEdit || typeof multiselectClass !== "function") return "";
            const index = Array.isArray(bandPath) ? bandPath[0] : 0;
            return multiselectClass("executive_summary", index, "executive_summary", band?.key ?? band?.label) || "";
        },
        [isMultiEdit, multiselectClass]
    );

    const openBandBuildingsModal = useCallback(band => {
        setBandBuildingsModal({
            bandLabel: getExecutiveSummaryBandLabel(band),
            buildings: getExecutiveSummaryBandBuildings(band)
        });
    }, []);

    const closeBandBuildingsModal = useCallback(() => {
        setBandBuildingsModal(null);
    }, []);

    const closeSampleTextViewer = useCallback(() => {
        setSampleTextViewer(null);
    }, []);

    const handleOpenTextBandFullEditor = useCallback(
        bandPath => {
            closeSampleTextViewer();
            if (typeof onOpenTextBandFullEditor === "function") {
                onOpenTextBandFullEditor(bandPath);
            }
        },
        [closeSampleTextViewer, onOpenTextBandFullEditor]
    );

    const openSampleTextViewer = useCallback((band, bandPath, rowKey) => {
        if (!isExecutiveSummarySampleTextBand(band)) return;
        const layout = computeSampleTextViewerLayoutForBandRow(rowKey, getBandRowDomId);
        setSampleTextViewer({
            rowKey,
            bandPath,
            label: getExecutiveSummaryBandLabel(band),
            sampleText: getExecutiveSummaryBandSampleText(band),
            editorKey: `exec-sum-sample-${rowKey}`,
            defaultSize: layout.size,
            initialPosition: layout.position
        });
    }, []);

    const handleBandQaStatusChange = useCallback(
        (bandPath, nextStatus) => {
            if (!onBandsChange) return;
            onBandsChange(updateBandAtPath(bands, bandPath, { qa_status: nextStatus }));
        },
        [bands, onBandsChange]
    );

    const leafBandRowProps = useMemo(
        () => ({
            editable,
            imageMap,
            allowImageAssignment: isMultiEdit ? false : allowImageAssignment,
            onToggleBand,
            onOpenTextBandFullEditor: handleOpenTextBandFullEditor,
            onOpenImageLayoutEditor,
            patchBandAtPath,
            canRemoveBand: effectiveCanRemoveBand,
            setPendingRemoveBandPath,
            setPendingRemoveImageSlot,
            onRequestRename: isMultiEdit ? undefined : openRenameBand,
            onRequestGalleryPick,
            onRequestBuildingPick,
            openBuildingPick,
            getBandSearchProps,
            getTextEditorSearchProps,
            getEditorWrapperClassName: isMultiEdit ? getEditorWrapperClassName : undefined,
            activeSearchMatch,
            debouncedSearchQuery,
            onShowBandBuildings: isMultiEdit ? openBandBuildingsModal : undefined,
            selectedBuildingCount: isMultiEdit ? selectedBuildingCount : 0,
            buildingCoverageTooltipId,
            bandAccordionTooltipId,
            bandFullEditorTooltipId,
            bandRenameTooltipId,
            bandRemoveTooltipId,
            optimizeTyping: isMultiEdit,
            useSimpleReadOnly: isLogCompare,
            showSampleTextEditor,
            onOpenSampleTextBandFullEditor,
            showSampleTextViewer: !showSampleTextEditor && !isMultiEdit && !isLogCompare,
            onOpenSampleTextViewer: openSampleTextViewer,
            bandSampleTextTooltipId,
            activeSampleTextViewerRowKey: sampleTextViewer?.rowKey ?? null,
            showBandQaStatus: effectiveShowBandQaStatus,
            canEditBandQaStatus,
            canMarkBandQaPassed,
            onBandQaStatusChange:
                effectiveShowBandQaStatus && canEditBandQaStatus && onBandsChange
                    ? handleBandQaStatusChange
                    : undefined,
            renderBandLeadingAddon
        }),
        [
            renderBandLeadingAddon,
            editable,
            imageMap,
            allowImageAssignment,
            isMultiEdit,
            isLogCompare,
            showSampleTextEditor,
            onOpenSampleTextBandFullEditor,
            openSampleTextViewer,
            sampleTextViewer,
            bandSampleTextTooltipId,
            effectiveShowBandQaStatus,
            canEditBandQaStatus,
            canMarkBandQaPassed,
            onBandsChange,
            onToggleBand,
            handleOpenTextBandFullEditor,
            onOpenImageLayoutEditor,
            patchBandAtPath,
            effectiveCanRemoveBand,
            onRequestGalleryPick,
            onRequestBuildingPick,
            openBuildingPick,
            getBandSearchProps,
            getTextEditorSearchProps,
            getEditorWrapperClassName,
            activeSearchMatch,
            debouncedSearchQuery,
            openRenameBand,
            openBandBuildingsModal,
            selectedBuildingCount,
            buildingCoverageTooltipId,
            bandAccordionTooltipId,
            bandFullEditorTooltipId,
            bandRenameTooltipId,
            bandRemoveTooltipId,
            handleBandQaStatusChange
        ]
    );

    const combBandRowSharedProps = useMemo(
        () => ({
            debouncedSearchQuery,
            activeSearchMatch,
            getTextEditorSearchProps
        }),
        [debouncedSearchQuery, activeSearchMatch, getTextEditorSearchProps]
    );

    const bandListBody = !bands?.length ? (
        <div className="text-muted small py-4 text-center">No executive summary sections yet.</div>
    ) : (
        <ul className="exec-sum-band-list">
            {bands.map((band, index) => {
                const bandPath = [index];
                if (isExecutiveSummaryCombBand(band)) {
                    const rowKey = bandPathToRowKey(bandPath, band);
                    return (
                        <CombBandRow
                            key={rowKey}
                            band={band}
                            bandPath={bandPath}
                            expandedBandKeys={expandedBandKeys}
                            editable={editable}
                            onToggleBand={onToggleBand}
                            onOpenAddBandChooser={onOpenAddBandChooser}
                            canRemoveBand={canRemoveBand}
                            setPendingRemoveBandPath={setPendingRemoveBandPath}
                            patchBandAtPath={patchBandAtPath}
                            onOpenTextBandFullEditor={handleOpenTextBandFullEditor}
                            leafBandRowProps={leafBandRowProps}
                            renderBandLeadingAddon={leafBandRowProps.renderBandLeadingAddon}
                            onRequestRename={openRenameBand}
                            onToggleGroupChildren={toggleBandRowKeys}
                            {...getBandSearchProps(rowKey)}
                            {...combBandRowSharedProps}
                        />
                    );
                }
                return (
                    <LeafBandRow
                        key={bandPathToRowKey(bandPath, band)}
                        band={band}
                        bandPath={bandPath}
                        expandedBandKeys={expandedBandKeys}
                        {...leafBandRowProps}
                    />
                );
            })}
        </ul>
    );

    return (
        <div ref={panelRef} className="exec-sum-panel building-exec-sum-scope">
            <ReactTooltip
                id={searchMatchCaseTooltipId}
                place="bottom"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
                html={true}
            />
            <ReactTooltip
                id={searchWholeWordTooltipId}
                place="bottom"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
                html={true}
            />
            <ReactTooltip
                id={bandAccordionTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            <ReactTooltip
                id={bandFullEditorTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            <ReactTooltip
                id={bulkExpandBandsTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            <ReactTooltip
                id={bandRenameTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            <ReactTooltip
                id={bandRemoveTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            <ReactTooltip
                id={bandSampleTextTooltipId}
                place="top"
                className="rc-tooltip-custom-class"
                effect="solid"
                backgroundColor="#007bff"
            />
            {isMultiEdit ? (
                <ReactTooltip
                    id={buildingCoverageTooltipId}
                    place="top"
                    className="rc-tooltip-custom-class"
                    effect="solid"
                    backgroundColor="#007bff"
                />
            ) : null}
            {bulkBandsBusy ? (
                <div className="exec-sum-panel__bulk-busy" role="status" aria-live="polite" aria-busy="true">
                    <Loader />
                    <p className="exec-sum-panel__bulk-busy-label">Expanding bands…</p>
                </div>
            ) : null}
            {renameBandCtx ? (
                <Portal
                    body={
                        <AddExecutiveSummaryModal
                            isRename
                            initialLabel={getExecutiveSummaryBandLabel(getBandAtPath(bands, renameBandCtx.bandPath))}
                            excludeBand={getBandAtPath(bands, renameBandCtx.bandPath)}
                            isImageBand={renameBandCtx.isImageBand}
                            isGroupBand={renameBandCtx.isGroupBand}
                            currentExecutiveSummary={bands}
                            onCancel={() => setRenameBandCtx(null)}
                            onSubmit={inputs => {
                                patchBandAtPath(renameBandCtx.bandPath, { label: inputs.label });
                                setRenameBandCtx(null);
                            }}
                        />
                    }
                    onCancel={() => setRenameBandCtx(null)}
                />
            ) : null}

            {allowImageAssignment && buildingPickCtx ? (
                <Portal
                    body={
                        <BuildingImagePickModal
                            buildingImages={buildingImages}
                            currentIds={buildingPickCtx.selectedIds}
                            loading={buildingPickImagesLoading}
                            onPick={applyBuildingPick}
                            onClose={() => {
                                buildingPickRequestRef.current += 1;
                                setBuildingPickCtx(null);
                                setBuildingPickImagesLoading(false);
                            }}
                        />
                    }
                    onCancel={() => {
                        buildingPickRequestRef.current += 1;
                        setBuildingPickCtx(null);
                        setBuildingPickImagesLoading(false);
                    }}
                />
            ) : null}

            {organizeModalOpen ? (
                <OrganizeBandsModal
                    bands={bands}
                    onApply={applyBandOrder}
                    onClose={() => setOrganizeModalOpen(false)}
                    saving={saving}
                    autoSave={!!onSave}
                />
            ) : null}

            {allowImageAssignment && pendingRemoveImageSlot != null ? (
                <Portal
                    body={
                        <div className="building-exec-sum-confirm-wrap">
                            <ConfirmationModal
                            type="cancel"
                            heading="Remove image?"
                            message={
                                <>
                                    Remove this image from the band?
                                    <br />
                                    You&apos;ll need to Save changes to persist.
                                </>
                            }
                            onNo={() => setPendingRemoveImageSlot(null)}
                            onYes={confirmRemoveImageSlot}
                            />
                        </div>
                    }
                    onCancel={() => setPendingRemoveImageSlot(null)}
                />
            ) : null}

            {pendingRemoveBandPath != null && removeBandConfirm ? (
                <Portal
                    body={
                        <div className="building-exec-sum-confirm-wrap">
                            <ConfirmationModal
                            type="cancel"
                            heading={removeBandConfirm.heading}
                            message={
                                <>
                                    {removeBandConfirm.lines.map((line, index) => (
                                        <React.Fragment key={line}>
                                            {index > 0 ? <br /> : null}
                                            {line}
                                        </React.Fragment>
                                    ))}
                                </>
                            }
                            onNo={() => setPendingRemoveBandPath(null)}
                            onYes={confirmRemoveBand}
                            />
                        </div>
                    }
                    onCancel={() => setPendingRemoveBandPath(null)}
                />
            ) : null}

            {pendingDiscardConfirm ? (
                <Portal
                    body={
                        <div className="building-exec-sum-confirm-wrap">
                            <ConfirmationModal
                            type="cancel"
                            heading="Discard executive summary changes?"
                            message={
                                <>
                                    You have unsaved edits to the executive summary.
                                    <br />
                                    Discard will restore the last saved version for this page.
                                </>
                            }
                            onNo={() => setPendingDiscardConfirm(false)}
                            onYes={() => {
                                setPendingDiscardConfirm(false);
                                if (onDiscard) onDiscard();
                            }}
                            />
                        </div>
                    }
                    onCancel={() => setPendingDiscardConfirm(false)}
                />
            ) : null}

            {!isLogCompare ? (
            <div ref={panelHeadRef} className="exec-sum-panel__head">
                <div className="exec-sum-panel__head-row1">
                <div className="exec-sum-panel__head-main">
                    {showAddBand ? (
                        <button
                            type="button"
                            className="exec-sum-toolbar-btn exec-sum-toolbar-btn--outline"
                            onClick={() => onOpenAddBandChooser(null)}
                        >
                            + Add band
                        </button>
                    ) : null}
                    {showSyncFromTemplate ? (
                        <button
                            type="button"
                            className="exec-sum-toolbar-btn exec-sum-toolbar-btn--ghost"
                            disabled={syncingFromTemplate}
                            onClick={() => onRequestSyncFromTemplate()}
                            data-tip="Adds missing bands from the project template and aligns existing bands with the template."
                            data-for={syncTemplateTooltipId}
                        >
                            {syncingFromTemplate ? (
                                <span
                                    className="spinner-border spinner-border-sm exec-sum-toolbar-btn__fa-icon"
                                    role="status"
                                    aria-hidden="true"
                                />
                            ) : (
                                <i className="fas fa-sync-alt exec-sum-toolbar-btn__fa-icon" aria-hidden="true" />
                            )}
                            Sync from template
                        </button>
                    ) : null}
                    {showOrganizeBands ? (
                        <button
                            type="button"
                            className="exec-sum-toolbar-btn exec-sum-toolbar-btn--ghost"
                            onClick={() => setOrganizeModalOpen(true)}
                        >
                            <span className="exec-sum-toolbar-btn__list-icon" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                            </span>
                            Organize bands
                        </button>
                    ) : null}
                    <ExecutiveSummaryBulkBandsToolbar
                        bands={bands}
                        expandedBandKeys={expandedBandKeys}
                        onExpandBands={onExpandBands}
                        onCollapseBands={onCollapseBands}
                        tooltipIdPrefix={tooltipIdPrefix}
                        includeTooltip={false}
                    />
                    {bands?.length > 0 ? (
                        <div
                            ref={searchAreaRef}
                            className={`exec-sum-panel__search${
                                debouncedSearchQuery ? " exec-sum-panel__search--active" : ""
                            }${searchOpen ? " exec-sum-panel__search--open" : ""}`}
                        >
                            <div className="exec-sum-panel__search-field">
                                <i className="fas fa-search exec-sum-panel__search-icon" aria-hidden="true" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="exec-sum-panel__search-input"
                                    placeholder="Find bands/content"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    onBlur={handleSearchBlur}
                                    onKeyDown={e => {
                                        if (e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                                            if (e.key.toLowerCase() === "c") {
                                                e.preventDefault();
                                                setSearchMatchCase(v => !v);
                                                return;
                                            }
                                            if (e.key.toLowerCase() === "w") {
                                                e.preventDefault();
                                                setSearchWholeWord(v => !v);
                                                return;
                                            }
                                        }
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            clearSearch();
                                            searchInputRef.current?.blur();
                                        } else if (e.key === "Enter") {
                                            e.preventDefault();
                                            goToSearchMatch(e.shiftKey ? -1 : 1);
                                        }
                                    }}
                                    aria-label="Search executive summary bands"
                                />
                                <button
                                    type="button"
                                    className={`exec-sum-panel__search-clear${
                                        searchQuery ? " exec-sum-panel__search-clear--visible" : ""
                                    }`}
                                    onClick={clearSearch}
                                    aria-label="Clear search"
                                    tabIndex={searchQuery ? 0 : -1}
                                    aria-hidden={searchQuery ? undefined : true}
                                >
                                    <i className="fas fa-times" aria-hidden="true" />
                                </button>
                            </div>
                            <div className="exec-sum-panel__search-options" role="group" aria-label="Search options">
                                <button
                                    type="button"
                                    className={`exec-sum-panel__search-option${
                                        searchMatchCase ? " exec-sum-panel__search-option--active" : ""
                                    }`}
                                    onClick={() => setSearchMatchCase(v => !v)}
                                    aria-pressed={searchMatchCase}
                                    aria-label="Upper and lower case matter"
                                    data-tip={EXEC_SUM_SEARCH_MATCH_CASE_TIP}
                                    data-html={true}
                                    data-for={searchMatchCaseTooltipId}
                                >
                                    Aa
                                </button>
                                <button
                                    type="button"
                                    className={`exec-sum-panel__search-option${
                                        searchWholeWord ? " exec-sum-panel__search-option--active" : ""
                                    }`}
                                    onClick={() => setSearchWholeWord(v => !v)}
                                    aria-pressed={searchWholeWord}
                                    aria-label="Match whole words only"
                                    data-tip={EXEC_SUM_SEARCH_WHOLE_WORD_TIP}
                                    data-html={true}
                                    data-for={searchWholeWordTooltipId}
                                >
                                    <span className="exec-sum-panel__search-option-whole">ab</span>
                                </button>
                            </div>
                            <div
                                className={`exec-sum-panel__search-results${
                                    debouncedSearchQuery ? " exec-sum-panel__search-results--visible" : ""
                                }`}
                                aria-live="polite"
                            >
                                {debouncedSearchQuery ? (
                                    searchMatches.length > 0 ? (
                                        <>
                                            <span className="exec-sum-panel__search-count">
                                                {activeSearchIndex + 1}/{searchMatches.length}
                                            </span>
                                            <button
                                                type="button"
                                                className="exec-sum-panel__search-step"
                                                onClick={() => goToSearchMatch(-1)}
                                                aria-label="Previous match"
                                            >
                                                <i className="fas fa-chevron-up" aria-hidden="true" />
                                            </button>
                                            <button
                                                type="button"
                                                className="exec-sum-panel__search-step"
                                                onClick={() => goToSearchMatch(1)}
                                                aria-label="Next match"
                                            >
                                                <i className="fas fa-chevron-down" aria-hidden="true" />
                                            </button>
                                        </>
                                    ) : (
                                        <span className="exec-sum-panel__search-none">No matches</span>
                                    )
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
                {effectiveShowBandQaStatus && qaBreakdown.totalCount > 0 ? (
                    <div className="exec-sum-panel__head-summary exec-sum-panel__head-summary--inline">
                        <ExecutiveSummaryQaProgressCard breakdown={qaBreakdown} />
                    </div>
                ) : null}
                {!isMultiEdit && ((editable && onDiscard) || (editable && onSave)) ? (
                    <div className="exec-sum-panel__head-actions-end">
                        {editable && onDiscard ? (
                            <button
                                type="button"
                                className="exec-sum-toolbar-btn exec-sum-toolbar-btn--ghost"
                                disabled={!effectiveDiscardDirty || saving}
                                onClick={() => setPendingDiscardConfirm(true)}
                            >
                                Discard changes
                            </button>
                        ) : null}
                        {editable && onSave ? (
                            <button
                                type="button"
                                className="exec-sum-toolbar-btn exec-sum-toolbar-btn--primary"
                                disabled={!dirty || saving}
                                onClick={() => onSave()}
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                </div>
            </div>
            ) : null}

            {isMultiEdit || isLogCompare ? (
                <div className={isLogCompare ? "exec-sum-log-compare-panel__scroll" : "exec-sum-panel__scroll"}>
                    {bandListBody}
                </div>
            ) : (
                bandListBody
            )}
            {bandBuildingsModal ? (
                <Portal
                    body={
                        <ExecSumBandBuildingsModal
                            key={bandBuildingsModal.bandLabel}
                            bandLabel={bandBuildingsModal.bandLabel}
                            buildings={bandBuildingsModal.buildings}
                            onClose={closeBandBuildingsModal}
                        />
                    }
                    onCancel={closeBandBuildingsModal}
                />
            ) : null}
            {sampleTextViewer ? (
                <ExecutiveSummarySampleTextViewer
                    key={sampleTextViewer.editorKey}
                    label={sampleTextViewer.label}
                    sampleText={sampleTextViewer.sampleText}
                    editorKey={sampleTextViewer.editorKey}
                    defaultSize={sampleTextViewer.defaultSize}
                    initialPosition={sampleTextViewer.initialPosition}
                    onClose={closeSampleTextViewer}
                />
            ) : null}
        </div>
    );
}

export default ExecutiveSummaryBandsPanel;
